// src/lib/api.ts（完全版：characterIpName 対応 + /character が data.lists の場合も吸収）
import { getDeviceParams } from "./device";
import { API_BASE_URL, API_FALLBACK_HOSTS } from "./config";
import { AppError } from "./errors";
import { sentryCaptureMessage } from "./sentry";
import { getByPath, pickNumberByPaths, pickStringByPaths } from "./path";
import { storage } from "./storage";
import type {
  CompanyResearchCandidateSearchInput,
  CompanyResearchCandidateSearchResponse,
  CompanyResearchCreateJobInput,
  CompanyResearchJobAccepted,
  CompanyResearchJobDetail,
  CompanyResearchJobList,
  CompanyResearchJobListInput,
  CompanyResearchQuickSummaryInput,
  CompanyResearchQuickSummaryResponse,
  CompanyResearchRecordDetail,
  CompanyResearchRecordList,
  CompanyResearchRecordListInput,
} from "./companyResearch";

type ApiEnvelope<T = any> = {
  code: number | string;
  message?: string;
  data?: T;
};

type CreateUserData = { id: string; secretKey: string };
type LoginData = { accessToken: string };
type EmailInviteLoginData = {
  email: string;
  userId: string;
  accessToken: string;
  isNewUser?: boolean;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
};

export type UserData = {
  email?: string;
  userName?: string;
  description?: string;
  intimacy?: any;

  // コイン残高
  totalAmount?: number;        // 現在の利用残高（有償＋無償）
  totalAmountPaid?: number;    // 現在の利用残高（有償）
  totalAmountFree?: number;    // 現在の利用残高（無償）

  currentCharacter?: {
    characterId?: string | number;
    characterIpName?: string; // ★モデル名
    characterName?: string;
    rankName?: string;
    rankUpPoint?: number;
    nextRankUpPoint?: number;
    description?: string;
    image?: string; // バックエンドから取得した画像パス（ドメイン除外）
    iconImage?: string; // バックエンドから取得したアイコン画像パス（ドメイン除外）
  };

  currentVoice?: { id?: string | number; name?: string };
};

export type CharacterData = {
  characterId?: string | number;
  id?: string | number;

  characterName?: string;
  name?: string;

  description?: string;
  messageToUser?: string;
  reportMemoToUser?: string;

  characterIpName?: string; // ★モデル名
  image?: string; // バックエンドから取得した画像パス（ドメイン除外）
  imageUrl?: string; // バックエンドから取得した画像 URL（フルURL形式）
  iconImage?: string; // バックエンドから取得したアイコン画像パス（ドメイン除外）
  iconImageUrl?: string; // バックエンドから取得したアイコン画像 URL（フルURL形式）
  isCurrent?: boolean;
  voice?: { id?: string | number; name?: string; label?: string } | null;
};

export type VoiceData = {
  id?: string | number;
  label?: string;
  name?: string;
  displayName?: string;
};

export type ChatHistoryItem = {
  id: string;
  seq?: number;
  date?: string;
  inout: number; // 0 user, 1 AI
  text: string;
  isGood?: number;
};

export type ChatHistoryData = {
  histories?: ChatHistoryItem[];
  has_more?: boolean;
  hasMore?: boolean;
  next_before_seq?: number | string | null;
  nextBeforeSeq?: number | string | null;
};

export type ChatHistoryResult = {
  histories: ChatHistoryItem[];
  hasMore: boolean;
  nextBeforeSeq: number | null;
};

export type ChatModeValue = 0 | 1;

export type ChatModeItem = {
  mode: ChatModeValue;
  name: string;
  unlocked: boolean;
  requiredRank: number | null;
};

export type ChatModesData = {
  modes: ChatModeItem[];
};

export type ChatTalkResponseData = {
  messageType?: string;
  responseType?: string;
  ttsModelName?: string;
  ttsLength?: string;
  speechFileUrl?: string | null;
  chatHistoryId?: string;
  message?: string | null;
  coord?: string;
  messages?: {
    id?: string | number | null;
    seq?: string | number | null;
    date?: string | null;
    inout?: string | number | null;
    text?: string | null;
    emotion?: string | number | null;
  };
  intimacy?: unknown | null;
  modes?: ChatModeItem[] | null;
};

export type EventLoginData = {
  status: number;
  this_points: number;
  current_rank: string;
  current_point: number;
  next_rankup_point: number;
  msg: string;
};

export type GoogleIntegrationConnectData = {
  scopeBundle: string;
  authorizationUrl: string;
  state: string;
  expiresInSeconds: number;
  resolvedScopeBundle?: string;
  resolvedScopes?: string[];
};

export type GoogleIntegrationStatusData = {
  connectionStatus: string;
  isConnected: boolean;
  scopeBundle: string;
  scopeBundleGranted: boolean;
  missingScopes?: string[];
  email?: string;
  connectedAt?: string;
  lastError?: string | null;
};

export type GoogleIntegrationDisconnectData = {
  disconnected: boolean;
  previousStatus?: string;
  connectionStatus?: string;
};

export type ReceiptJobCreateData = {
  jobId: string;
  clientRequestId: string;
  status: "accepted";
  acceptedAt?: string;
};

export type ReceiptJobStatusData = {
  jobId: string;
  clientRequestId: string;
  status: "accepted" | "completed" | "failed";
  processedAt?: string | null;
  extractedSummary?: string | null;
  failedStage?: string | null;
  failedError?: string | null;
};

export type ReceiptStructuredResult = {
  merchantName: string;
  receiptDate: string;
  totalAmount: string;
  currency: string;
  taxAmount: string;
  paymentMethod: string;
  invoiceNumber: string;
  accountCategory: string;
  summary: string;
};

export type ReceiptDetailData = {
  jobId: string;
  relayStatus: "accepted" | "completed" | "failed";
  needsReview: boolean;
  reviewedAt: string | null;
  userNotes: string | null;
  structuredResult: ReceiptStructuredResult | null;
  confidence: number | null;
  driveFileId: string | null;
  sheetId: string | null;
  processedAt: string | null;
  failedStage: string | null;
  failedError: string | null;
};

export type ReceiptReviewResult = {
  jobId: string;
  needsReview: boolean;
  reviewedAt: string;
};

export type ReceiptSourceImageData = {
  url: string;
  expiresAt: string;
};
export type ReceiptHistoryItem = {
  jobId: string;
  relayStatus: "accepted" | "completed" | "failed";
  needsReview: boolean;
  merchantName: string | null;
  receiptDate: string | null;
  totalAmount: string | null;
  currency: string | null;
  driveFileId: string | null;
  processedAt: string | null;
  reviewedAt: string | null;
};

export type ReceiptHistoryList = {
  data: ReceiptHistoryItem[];
  meta: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
};

export type ExecutiveMemoRelayStatus =
  | "accepted"
  | "processing"
  | "completed"
  | "failed"
  | "timed_out";

export type ExecutiveMemoFailedStage =
  | "validate"
  | "relay"
  | "ocr"
  | "structure"
  | "persist";

export type ExecutiveMemoPriority = "high" | "mid" | "low";
export type ExecutiveMemoActionItemType = "todo" | "check" | "consult";

export type ExecutiveMemoJobCreateData = {
  jobId: string;
  relayStatus: "accepted";
  acceptedAt: string;
  callbackDueAt?: string | null;
};

export type ExecutiveMemoJobStatusData = {
  jobId: string;
  clientRequestId?: string | null;
  relayStatus: ExecutiveMemoRelayStatus;
  externalStatus?: string | null;
  memoId?: string | null;
  requestedAt?: string | null;
  acceptedAt?: string | null;
  processedAt?: string | null;
  callbackDueAt?: string | null;
  failedStage?: ExecutiveMemoFailedStage | null;
  failedError?: string | null;
};

export type ExecutiveMemoListItem = {
  id: string;
  title?: string | null;
  bodyPreview: string;
  summary?: string | null;
  kind?: string | null;
  themes: string[];
  tags: string[];
  priority: ExecutiveMemoPriority;
  capturedAt: string;
  status: "active" | "archived";
  updatedAt?: string | null;
};

export type ExecutiveMemoDetailData = ExecutiveMemoListItem & {
  body: string;
  ocrRawText?: string | null;
  actionItems: { type: ExecutiveMemoActionItemType; text: string }[];
  relatedAgents: string[];
  sourceImage: { available: boolean; expiresAt?: string | null };
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type ExecutiveMemoListData = {
  items: ExecutiveMemoListItem[];
  nextCursor?: string | null;
};

export type ExecutiveMemoJobListData = {
  items: ExecutiveMemoJobStatusData[];
  nextCursor?: string | null;
};

export type ExecutiveMemoSourceImageData = {
  url: string;
  expiresAt: string;
};

export type ExecutiveMemoUpdatePayload = {
  version: number;
  title?: string;
  body?: string;
  kind?: string;
  themes?: string[];
  tags?: string[];
  priority?: ExecutiveMemoPriority;
  actionItems?: { type: ExecutiveMemoActionItemType; text: string }[];
  relatedAgents?: string[];
  status?: "active" | "archived";
};

export type LineLinkCodeData = {
  link_code: string;
  expires_at: string;
  instruction: string;
};

export type LineOAuthAuthorizeData = {
  authorizeUrl: string;
  state: string;
  expiresAt?: string | null;
};

export type LineOAuthCallbackData = {
  status?: string;
  isLinked?: boolean;
  notificationUserId?: string;
  lineDisplayName?: string;
  linkedAt?: string;
  friendshipStatusChanged?: boolean | null;
};

export type LineLinkStatusData = {
  status?: string;
  isLinked?: boolean;
  notificationUserId?: string;
  lineDisplayName?: string;
  linkedAt?: string;
};

export type LineLinkDisconnectData = {
  disconnected: boolean;
  previousStatus?: string;
  status?: string;
};

export type ProfileMasterItem = {
  key: string;
  label: string;
  displayOrder: number;
  valueType?: string | null;
};

export type UserProfileItem = {
  key: string;
  label: string;
  value: string;
  displayOrder: number;
};

export type UserProfileUpdateItem = {
  key: string;
  value: string;
};

export type GroupTalkSessionRole = "facilitator" | "expert";
export type GroupTalkSpeakerType = "user" | "facilitator" | "expert";

export type GroupTalkSessionStateDto = {
  current_category?: string;
  current_focus_point?: string;
  open_questions?: string[];
  decision_status?: string;
};

export type GroupTalkParticipantDto = {
  participant_id: string;
  member_id: string;
  speaker_id: string;
  display_name?: string | null;
  role: GroupTalkSessionRole;
  stance?: string | null;
};

export type GroupTalkMessageDto = {
  message_id: string;
  turn_id?: string | null;
  participant_id?: string | null;
  member_id?: string | null;
  speaker_id: string;
  speaker_name: string;
  speaker_type: GroupTalkSpeakerType;
  phase?: string | null;
  sequence?: number | null;
  content: string;
  next_choices?: GroupTalkNextChoiceDto[] | null;
  created_at?: string | null;
};

export type GroupTalkNextChoiceDto = {
  label?: string | null;
  value?: string | null;
  choice_type?: string | null;
  is_recommended?: boolean | null;
  requires_input?: boolean | null;
  input_prompt?: string | null;
  input_placeholder?: string | null;
};

export type GroupTalkSessionHistoryItemResponse = {
  session_id: string;
  title?: string | null;
  first_user_message?: string | null;
  updated_at: string;
  created_at?: string | null;
  member_summary?: string[] | null;
  last_message_preview?: string | null;
};

export type GroupTalkSessionHistoryListResponse = {
  sessions: GroupTalkSessionHistoryItemResponse[];
};

export type GroupTalkSessionResponse = {
  session_id: string;
  template_id?: string | null;
  title: string;
  description?: string | null;
  members: GroupTalkParticipantDto[];
  agreed_notes?: string[] | string | null;
  session_state?: GroupTalkSessionStateDto | null;
  status?: string;
  created_at: string;
};

export type GroupTalkSessionDetailResponse = GroupTalkSessionResponse & {
  messages: GroupTalkMessageDto[];
  updated_at?: string | null;
};

export type GroupTalkTurnResponse = {
  session_id: string;
  turn_id: string;
  messages: GroupTalkMessageDto[];
  agreed_notes?: string[] | string | null;
  session_state?: GroupTalkSessionStateDto | null;
  is_satisfied?: boolean;
  next_choices?: GroupTalkNextChoiceDto[] | null;
  created_at: string;
};

export type GroupTalkIntakeResponse = {
  session_id: string;
  turn_id: string;
  needs_follow_up?: boolean;
  follow_up_question?: string | null;
  continue_token?: string | null;
  messages: GroupTalkMessageDto[];
  agreed_notes?: string[] | string | null;
  session_state?: GroupTalkSessionStateDto | null;
  next_choices?: GroupTalkNextChoiceDto[] | null;
  created_at: string;
};

export type GroupTalkReportStatus = "not_generated" | "generating" | "completed" | "failed";

export type GroupTalkReportResponse = {
  status: GroupTalkReportStatus;
  title?: string;
  markdown_body?: string;
  generated_at?: string;
  source_hash?: string;
  error_message?: string;
  retryable?: boolean;
  is_stale?: boolean;
  can_regenerate?: boolean;
};

export type CreateGroupTalkSessionParams = {
  templateId: string;
  title: string;
  description: string;
  members: Array<{
    participantId: string;
    memberId: string;
    speakerId: string;
    role: GroupTalkSessionRole;
    stance?: string | null;
  }>;
};

export type CreateGroupTalkTurnParams = {
  userMessage: string;
};

export type CompleteGroupTalkTurnParams = {
  continueToken: string;
};

export type GroupTalkRecommendationStanceDto = {
  stance?: string | null;
  label?: string | null;
  description?: string | null;
};

export type GroupTalkRecommendationMemberDto = {
  role?: string | null;
  member_id?: string | null;
  character_name?: string | null;
  display_role?: string | null;
  default_stance?: string | null;
  stance_label?: string | null;
  available_stances?: GroupTalkRecommendationStanceDto[] | null;
  image_variant?: number | null;
  is_fixed?: boolean | null;
};

export type GroupTalkRecommendationResponse = {
  recommended_template?: string | null;
  template_label?: string | null;
  template_description?: string | null;
  reason?: string | null;
  confidence?: string | null;
  recommended_members?: GroupTalkRecommendationMemberDto[] | null;
  optional_members?: GroupTalkRecommendationMemberDto[] | null;
};

const GROUP_TALK_COMPLETE_CLIENT_TIMEOUT_MS = 130_000;

// ====== コイン購入関連の型定義 ======

// 商品（コインプラン）の型定義
export type Product = {
  id: string;                 // アプリ内課金商品ID
  storeType: 1 | 2;          // 1:App Store, 2:Google Play
  productType: 1 | 2;        // 1:サブスク, 2:コイン
  productId: string;         // プロダクトID（例: jp.co.xxxx.consumable.item1）
  name: string;              // 商品名（例: "コイン100"）
  price: number;             // 商品価格
  coin: number;              // 有償コイン数
  free_coin: number;         // 無償コイン数
  description: string;       // 商品説明
  itemCode: string;          // アイテムコード
  imageFileName?: string;    // 商品画像ファイル名
  salesFrom: string;         // 販売期間（from）ISO 8601
  salesTo: string;           // 販売期間（to）ISO 8601
  isOneTime: 0 | 1;         // 0:何度でも購入可能, 1:一回限り購入可能
  isPurchased: 0 | 1;       // 0:未購入, 1:購入済
  createdAt: string;         // 作成日時
  updatedAt: string;         // 更新日時
};

// プラン一覧レスポンス
export type ProductListResponse = {
  lists: Product[];
};

// 購入リクエスト
export type ProductPurchaseRequest = {
  iapProductId?: string;     // アプリ内課金商品ID（0円商品以外は必須）
  storeType: 1 | 2;          // 1:App Store, 2:Google Play
  transactionId?: string;    // トランザクションID（0円商品以外は必須）
  purchaseToken?: string;    // 購入トークン
};

// 購入レスポンス
export type ProductPurchaseResponse = {
  // レスポンスにdataフィールドなし（購入後は /user を再取得して最新残高を確認）
};

export type NewsNotificationType = "line" | "google_chat" | "email";

export type NewsJobMode = "news" | "report";
export type NewsReadingProfile = "grasp" | "organize" | "think";
export type NewsDeliveryOption = "text" | "audio" | "text_and_audio";

export type NewsJobRequest = {
  keyword: string;
  notificationType: NewsNotificationType;
  email?: string;
  redirectUrl?: string;
  mode?: NewsJobMode;
  readingProfile?: NewsReadingProfile;
  deliveryOption?: NewsDeliveryOption;
  userProfile?: string;
};

export type NewsJobResponse = {
  id: string;
  jobId: string;
  status: "accepted";
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
};

export type NewsJobStatusData = {
  id: string;
  jobId: string;
  status: "accepted" | "completed" | "failed";
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
  processedAt?: string | null;
  failedStage?: string | null;
  errorMessage?: string | null;
  mode?: NewsJobMode | null;
  readingProfile?: NewsReadingProfile | null;
  callbackMessage?: string | null;
  summary?: string | null;
};

export type NewsReportSubscriptionMode = "news" | "report";
export type NewsReportSubscriptionFrequency = "daily" | "weekly" | "monthly";
export type NewsReportSubscriptionStatus = "active" | "paused" | "disabled";
export type NewsReportSubscriptionNotificationType = "line" | "email" | "google_chat";
export type NewsReportSubscriptionDeliveryFormat = "text" | "text_and_audio";

export type NewsReportSubscriptionScheduleConfig =
  | { type: "daily" }
  | { type: "weekly"; days_of_week: number[] }
  | { type: "monthly"; monthly_mode: "day_of_month"; days_of_month: number[] }
  | { type: "monthly"; monthly_mode: "last_day" };

export type NewsReportSubscription = {
  id: string;
  mode: NewsReportSubscriptionMode;
  title?: string | null;
  keyword: string;
  frequency: NewsReportSubscriptionFrequency;
  scheduleConfig?: NewsReportSubscriptionScheduleConfig | null;
  deliveryTime: string;
  timezone: string;
  notificationType: NewsReportSubscriptionNotificationType;
  email?: string | null;
  emailMasked?: string | null;
  deliveryFormat: NewsReportSubscriptionDeliveryFormat;
  readingProfile?: string | null;
  status: NewsReportSubscriptionStatus;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type NewsReportSubscriptionList = {
  items: NewsReportSubscription[];
  nextCursor?: string | null;
};

export type NewsReportSubscriptionRunNowResponse = {
  runId: string;
  status: string;
  agentJobId?: string | null;
};

export type NewsReportSubscriptionCreateRequest = {
  mode: NewsReportSubscriptionMode;
  title?: string;
  keyword: string;
  frequency: NewsReportSubscriptionFrequency;
  schedule_config: NewsReportSubscriptionScheduleConfig;
  delivery_time: string;
  timezone: string;
  notification_type: NewsReportSubscriptionNotificationType;
  email?: string;
  delivery_format: NewsReportSubscriptionDeliveryFormat;
  reading_profile: string;
  created_by_channel: "web";
};

export type NewsReportSubscriptionUpdateRequest = Partial<
  Omit<NewsReportSubscriptionCreateRequest, "created_by_channel">
>;

export type AgentJobResource =
  | "news"
  | "report"
  | "receipt"
  | "executive_memo"
  | "company_research"
  | (string & {});

export type AgentJobStatus =
  | "accepted"
  | "running"
  | "completed"
  | "failed"
  | "needs_input"
  | (string & {});

export type AgentJobItem = {
  id?: string;
  resource: AgentJobResource;
  jobId?: string;
  job_id?: string;
  status: AgentJobStatus;
  rawStatus?: string;
  raw_status?: string;
  title?: string | null;
  summary?: string | null;
  resultSummary?: string | null;
  result_summary?: string | null;
  requestedAt?: string | null;
  requested_at?: string | null;
  acceptedAt?: string | null;
  accepted_at?: string | null;
  processedAt?: string | null;
  processed_at?: string | null;
  failedAt?: string | null;
  failed_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
  errorMessage?: string | null;
  error_message?: string | null;
  detailApiPath?: string | null;
  detail_api_path?: string | null;
  meta?: unknown;
};

export type AgentJobsData = {
  items: AgentJobItem[];
  nextCursor?: string | null;
  next_cursor?: string | null;
};

export type LineRegisterStartData = {
  authorizeUrl: string;
  expiresAt?: string | null;
};

export type LineRegisterCallbackData = {
  registrationToken: string;
  expiresAt?: string | null;
};

export type LineRegisterCompleteData = {
  userId: string;
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  isNewUser?: boolean;
};

export type GoogleSsoInvalidateResult = {
  invalidated: boolean;
  alreadyInvalidated?: boolean;
  effectiveFrom?: string | null;
};

export type AuthStatus = "anonymous" | "authenticated";
export type ProfileStatus = "incomplete" | "complete";
export type PlanType = "none" | "free" | "paid" | "invite_trial" | "internal";
export type EntitlementStatus = "none" | "active" | "expired";

export type FeatureKey =
  | "chat"
  | "group_talk"
  | "news"
  | "report"
  | "company_research"
  | "receipt_ocr"
  | "memo_ocr"
  | "business_card_ocr"
  | "schedule_agent"
  | "travel_agent"
  | "reply_agent"
  | "shopping_agent"
  | "google_integration"
  | "admin";

export type FeatureLimits = Record<string, unknown>;

export type FeatureUsageCounter = {
  limit: number;
  used: number;
  remaining: number;
};

export type FeatureUsage = Record<string, Record<string, FeatureUsageCounter>>;

export type AccountState = {
  authStatus: AuthStatus;
  profileStatus: ProfileStatus;
  plan: PlanType;
  planCode: string | null;
  entitlementStatus: EntitlementStatus;
  entitlementExpiresAt: string | null;
  featureScope: FeatureKey[];
  featureLimits: FeatureLimits;
  featureUsage?: FeatureUsage;
  suggestedCallName: string | null;
  authIdentities: Record<string, { linked: boolean }>;
  externalIntegrations: Record<string, unknown>;
  billingEnabled: boolean;
};

export type InviteApplyErrorCode =
  | "invalid_invite_code"
  | "invite_code_not_started"
  | "invite_code_expired"
  | "invite_code_usage_limit_exceeded"
  | "invite_code_inactive";

export type BillingCheckoutSessionData = {
  provider: "stripe";
  sessionId: string;
  url: string;
};

export type BillingPortalSessionData = {
  provider: "stripe";
  url: string;
};

export type BillingSubscriptionData = {
  provider: "stripe";
  status: string | null;
  plan: PlanType | null;
  planCode: string | null;
  currentPeriodEnd: string | null;
  paidThroughAt: string | null;
  cancelAtPeriodEnd: boolean;
  managementAvailable: boolean;
  accountState: AccountState;
};

export type BillingErrorCode =
  | "billing_subscription_already_active"
  | "billing_customer_not_found";

export type InviteApplyGrant = {
  plan: PlanType;
  planCode: string | null;
  entitlementStatus: EntitlementStatus;
  entitlementExpiresAt: string | null;
  featureScope: FeatureKey[];
  featureLimits: FeatureLimits;
};

export type InviteApplyResult = {
  applied: boolean;
  alreadyApplied: boolean;
  grant?: InviteApplyGrant;
  accountState: AccountState;
  errorCode?: InviteApplyErrorCode | null;
};

export type PrivilegeApplyErrorCode = "invalid_privilege_code";

export type PrivilegeApplyResult = {
  applied: boolean;
  alreadyApplied: boolean;
  accountState: AccountState;
};

type GoogleSsoData = {
  email: string;
  userId?: string;
  secretKey?: string;
  accessToken?: string;
  refreshToken?: string;
  isNewUser?: boolean;
};

type AuthSession = {
  userId: string;
  secretKey?: string;
  accessToken: string;
};

// ====== 設定 ======
const API_PREFIX = "/api/v1";

// バックエンドが返す招待コード要求メッセージ。api.ts 内でのみ参照する。
// バックエンドのメッセージが変わった場合はここを更新すること。
const INVITE_CODE_REQUIRED_MESSAGE = "招待コードが必要です。" as const;

function normalizeBaseURL(v: string) {
  if (!v) return "";
  return v.endsWith("/") ? v.slice(0, -1) : v;
}
const NORMALIZED_API_BASE_URL = normalizeBaseURL(API_BASE_URL);

// ====== util ======
function safeText(t: string, limit = 2000) {
  if (!t) return "";
  return t.length > limit ? t.slice(0, limit) + "..." : t;
}

function statusToCode(status: number) {
  if (status === 400) return "BAD_REQUEST" as const;
  if (status === 401) return "UNAUTHORIZED" as const;
  if (status === 403) return "FORBIDDEN" as const;
  if (status === 404) return "NOT_FOUND" as const;
  if (status === 409) return "CONFLICT" as const;
  if (status === 503) return "MAINTENANCE" as const;
  if (status >= 500) return "SERVER" as const;
  return "UNKNOWN" as const;
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

function buildPath(pathWithoutPrefix: string) {
  if (!pathWithoutPrefix.startsWith("/")) {
    throw new AppError("Invalid API path (must start with '/').", "CONFIG", {
      endpoint: pathWithoutPrefix,
      method: "UNKNOWN",
    });
  }
  return `${API_PREFIX}${pathWithoutPrefix}`;
}

function buildEndpoint(fullPath: string) {
  if (!fullPath.startsWith("/")) {
    throw new AppError("Invalid API path (must start with '/').", "CONFIG", {
      endpoint: fullPath,
      method: "UNKNOWN",
    });
  }

  if (!NORMALIZED_API_BASE_URL) return fullPath;

  // REACT_APP_API_BASE_URL が "/api" のようなパス指定の場合
  if (NORMALIZED_API_BASE_URL.startsWith("/")) {
    // fullPath が既に "/api/..." を含んでいるなら二重付与しない
    if (fullPath === NORMALIZED_API_BASE_URL || fullPath.startsWith(`${NORMALIZED_API_BASE_URL}/`)) {
      return fullPath;
    }
    return `${NORMALIZED_API_BASE_URL}${fullPath}`;
  }

  // "https://example.com" のような絶対URLの場合
  return `${NORMALIZED_API_BASE_URL}${fullPath}`;
}

function unwrapList<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data.lists)) return data.lists as T[];
  return [];
}

function normalizeChatModeValue(value: unknown): ChatModeValue | null {
  if (value === 0 || value === 1) return value;
  if (value === "0" || value === "1") return Number(value) as ChatModeValue;
  return null;
}

function normalizeChatModeItem(value: any): ChatModeItem | null {
  const mode = normalizeChatModeValue(value?.mode);
  const name = typeof value?.name === "string" ? value.name.trim() : "";

  let unlocked: boolean | null = null;
  if (typeof value?.unlocked === "boolean") {
    unlocked = value.unlocked;
  } else if (typeof value?.unlocked === "number") {
    unlocked = value.unlocked !== 0;
  } else if (typeof value?.unlocked === "string") {
    const lowered = value.unlocked.trim().toLowerCase();
    if (lowered === "true" || lowered === "1") unlocked = true;
    if (lowered === "false" || lowered === "0") unlocked = false;
  }

  const rawRequiredRank = value?.requiredRank ?? value?.required_rank;
  let requiredRank: number | null = null;
  if (rawRequiredRank !== undefined && rawRequiredRank !== null && rawRequiredRank !== "") {
    const normalizedRequiredRank = Number(rawRequiredRank);
    if (Number.isFinite(normalizedRequiredRank)) {
      requiredRank = normalizedRequiredRank;
    }
  }

  if (mode === null || !name || unlocked === null) return null;

  return {
    mode,
    name,
    unlocked,
    requiredRank: mode === 0 ? null : requiredRank,
  };
}

function pickBooleanByPaths(obj: any, paths: string[]): boolean | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const lowered = value.trim().toLowerCase();
      if (["true", "1", "linked", "active", "connected"].includes(lowered)) return true;
      if (["false", "0", "unlinked", "inactive", "disconnected"].includes(lowered)) return false;
    }
  }

  return undefined;
}

const FEATURE_KEYS = [
  "chat",
  "group_talk",
  "news",
  "report",
  "company_research",
  "receipt_ocr",
  "memo_ocr",
  "business_card_ocr",
  "schedule_agent",
  "travel_agent",
  "reply_agent",
  "shopping_agent",
  "google_integration",
  "admin",
] as const satisfies readonly FeatureKey[];

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeAuthStatus(value: unknown): AuthStatus {
  return value === "authenticated" ? "authenticated" : "anonymous";
}

function normalizeProfileStatus(value: unknown): ProfileStatus {
  return value === "complete" ? "complete" : "incomplete";
}

function normalizePlanType(value: unknown): PlanType {
  if (value === "free" || value === "paid" || value === "invite_trial" || value === "internal") {
    return value;
  }
  return "none";
}

function normalizeEntitlementStatus(value: unknown): EntitlementStatus {
  if (value === "active" || value === "expired") return value;
  return "none";
}

function normalizeFeatureScope(value: unknown): FeatureKey[] {
  if (!Array.isArray(value)) return [];
  const knownFeatureKeys = new Set<string>(FEATURE_KEYS);
  return value.filter((item): item is FeatureKey => typeof item === "string" && knownFeatureKeys.has(item));
}

function normalizeFeatureLimits(value: unknown): FeatureLimits {
  return isPlainRecord(value) ? value : {};
}

function normalizeFeatureUsage(value: unknown): FeatureUsage {
  if (!isPlainRecord(value)) return {};

  const normalized: FeatureUsage = {};
  Object.entries(value).forEach(([feature, limits]) => {
    if (!isPlainRecord(limits)) return;

    const normalizedLimits: Record<string, FeatureUsageCounter> = {};
    Object.entries(limits).forEach(([limitKey, counter]) => {
      if (!isPlainRecord(counter)) return;

      const limit = typeof counter.limit === "number" ? counter.limit : Number(counter.limit);
      const used = typeof counter.used === "number" ? counter.used : Number(counter.used);
      const remaining = typeof counter.remaining === "number" ? counter.remaining : Number(counter.remaining);
      if (!Number.isFinite(limit) || !Number.isFinite(used) || !Number.isFinite(remaining)) return;

      normalizedLimits[limitKey] = { limit, used, remaining };
    });

    if (Object.keys(normalizedLimits).length > 0) {
      normalized[feature] = normalizedLimits;
    }
  });

  return normalized;
}

function normalizeLinkedRecord(value: unknown): Record<string, { linked: boolean }> {
  if (!isPlainRecord(value)) return {};

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => {
      const linked = isPlainRecord(item) ? pickBooleanByPaths(item, ["linked", "isLinked", "is_linked"]) ?? false : false;
      return [key, { linked }];
    })
  );
}

function normalizeAccountState(data: any): AccountState {
  return {
    authStatus: normalizeAuthStatus(data?.authStatus ?? data?.auth_status),
    profileStatus: normalizeProfileStatus(data?.profileStatus ?? data?.profile_status),
    plan: normalizePlanType(data?.plan),
    planCode: pickStringByPaths(data, ["planCode", "plan_code"]) ?? null,
    entitlementStatus: normalizeEntitlementStatus(data?.entitlementStatus ?? data?.entitlement_status),
    entitlementExpiresAt: pickStringByPaths(data, ["entitlementExpiresAt", "entitlement_expires_at"]) ?? null,
    featureScope: normalizeFeatureScope(data?.featureScope ?? data?.feature_scope),
    featureLimits: normalizeFeatureLimits(data?.featureLimits ?? data?.feature_limits),
    featureUsage: normalizeFeatureUsage(data?.featureUsage ?? data?.feature_usage),
    suggestedCallName: pickStringByPaths(data, ["suggestedCallName", "suggested_call_name"]) ?? null,
    authIdentities: normalizeLinkedRecord(data?.authIdentities ?? data?.auth_identities),
    externalIntegrations: isPlainRecord(data?.externalIntegrations ?? data?.external_integrations)
      ? (data?.externalIntegrations ?? data?.external_integrations)
      : {},
    billingEnabled: typeof data?.billingEnabled === "boolean" ? data.billingEnabled : false,
  };
}

function normalizeInviteApplyErrorCode(value: unknown): InviteApplyErrorCode | null {
  if (
    value === "invalid_invite_code" ||
    value === "invite_code_not_started" ||
    value === "invite_code_expired" ||
    value === "invite_code_usage_limit_exceeded" ||
    value === "invite_code_inactive"
  ) {
    return value;
  }
  return null;
}

function normalizeInviteApplyGrant(value: unknown): InviteApplyGrant | undefined {
  if (!isPlainRecord(value)) return undefined;

  return {
    plan: normalizePlanType(value.plan),
    planCode: pickStringByPaths(value, ["planCode", "plan_code"]) ?? null,
    entitlementStatus: normalizeEntitlementStatus(value.entitlementStatus ?? value.entitlement_status),
    entitlementExpiresAt: pickStringByPaths(value, ["entitlementExpiresAt", "entitlement_expires_at"]) ?? null,
    featureScope: normalizeFeatureScope(value.featureScope ?? value.feature_scope),
    featureLimits: normalizeFeatureLimits(value.featureLimits ?? value.feature_limits),
  };
}

function normalizeInviteApplyResult(data: any): InviteApplyResult {
  return {
    applied: pickBooleanByPaths(data, ["applied"]) ?? false,
    alreadyApplied: pickBooleanByPaths(data, ["alreadyApplied", "already_applied"]) ?? false,
    grant: normalizeInviteApplyGrant(data?.grant),
    accountState: normalizeAccountState(data?.accountState ?? data?.account_state),
    errorCode: normalizeInviteApplyErrorCode(data?.errorCode ?? data?.error_code),
  };
}

function normalizeProfileMasterItem(data: any): ProfileMasterItem | null {
  const key = pickStringByPaths(data, ["key", "profileKey", "profile_key", "master.key"]);
  const label = pickStringByPaths(data, ["label", "name", "displayName", "display_name", "master.label"]);
  const displayOrder =
    pickNumberByPaths(data, ["displayOrder", "display_order", "master.displayOrder", "master.display_order"]) ?? 0;

  if (!key || !label) {
    return null;
  }

  return {
    key,
    label,
    displayOrder,
    valueType: pickStringByPaths(data, ["valueType", "value_type", "master.valueType", "master.value_type"]),
  };
}

function normalizeUserProfileItem(data: any): UserProfileItem | null {
  const key = pickStringByPaths(data, ["key", "profileKey", "profile_key", "master.key"]);
  const label = pickStringByPaths(data, ["label", "name", "displayName", "display_name", "master.label"]);
  const displayOrder =
    pickNumberByPaths(data, ["displayOrder", "display_order", "master.displayOrder", "master.display_order"]) ?? 0;
  const value =
    pickStringByPaths(data, ["value", "profileValue", "profile_value", "userProfile.value", "user_profile.value"]) ?? "";

  if (!key || !label) {
    return null;
  }

  return {
    key,
    label,
    value,
    displayOrder,
  };
}

function pickStringArrayByPaths(obj: any, paths: string[]): string[] | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (!Array.isArray(value)) continue;

    const normalized = value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter((item) => item.length > 0);
    return normalized;
  }

  return undefined;
}

function pickStringArrayOrDelimitedByPaths(obj: any, paths: string[]): string[] | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter((item) => item.length > 0);
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => (typeof item === "string" ? item.trim() : ""))
              .filter((item) => item.length > 0);
          }
        } catch {
          // fall through to comma split
        }
      }
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
  }

  return undefined;
}

function joinQueryValues(values?: string[]) {
  const normalized = values?.map((value) => value.trim()).filter((value) => value.length > 0) ?? [];
  return normalized.length ? normalized.join(",") : undefined;
}

function normalizeExecutiveMemoRelayStatus(value: unknown): ExecutiveMemoRelayStatus {
  if (
    value === "accepted" ||
    value === "processing" ||
    value === "completed" ||
    value === "failed" ||
    value === "timed_out"
  ) {
    return value;
  }

  return "processing";
}

function normalizeExecutiveMemoFailedStage(value: unknown): ExecutiveMemoFailedStage | null {
  if (
    value === "validate" ||
    value === "relay" ||
    value === "ocr" ||
    value === "structure" ||
    value === "persist"
  ) {
    return value;
  }

  return null;
}

function normalizeExecutiveMemoPriority(value: unknown): ExecutiveMemoPriority {
  if (value === "high" || value === "mid" || value === "low") return value;
  return "mid";
}

function normalizeExecutiveMemoStatus(value: unknown): "active" | "archived" {
  return value === "archived" ? "archived" : "active";
}

function normalizeExecutiveMemoActionItemType(value: unknown): ExecutiveMemoActionItemType {
  if (value === "check" || value === "consult") return value;
  return "todo";
}

function normalizeExecutiveMemoActionItems(value: unknown) {
  if (!Array.isArray(value)) return [] as { type: ExecutiveMemoActionItemType; text: string }[];

  return value
    .map((item) => {
      const text = pickStringByPaths(item, ["text", "label", "body"]) ?? "";
      return {
        type: normalizeExecutiveMemoActionItemType(pickStringByPaths(item, ["type", "item_type", "action_type"])),
        text,
      };
    })
    .filter((item) => item.text.trim().length > 0);
}

function normalizeExecutiveMemoJobStatus(data: any): ExecutiveMemoJobStatusData {
  const jobId = pickStringByPaths(data, ["jobId", "job_id", "id"]) ?? "";
  return {
    jobId,
    clientRequestId: pickStringByPaths(data, ["clientRequestId", "client_request_id"]) ?? null,
    relayStatus: normalizeExecutiveMemoRelayStatus(
      pickStringByPaths(data, ["relayStatus", "relay_status", "status"])
    ),
    externalStatus: pickStringByPaths(data, ["externalStatus", "external_status"]) ?? null,
    memoId: pickStringByPaths(data, ["memoId", "memo_id", "executiveMemoId", "executive_memo_id"]) ?? null,
    requestedAt: pickStringByPaths(data, ["requestedAt", "requested_at"]) ?? null,
    acceptedAt: pickStringByPaths(data, ["acceptedAt", "accepted_at"]) ?? null,
    processedAt: pickStringByPaths(data, ["processedAt", "processed_at"]) ?? null,
    callbackDueAt: pickStringByPaths(data, ["callbackDueAt", "callback_due_at"]) ?? null,
    failedStage: normalizeExecutiveMemoFailedStage(
      pickStringByPaths(data, ["failedStage", "failed_stage"])
    ),
    failedError: pickStringByPaths(data, ["failedError", "failed_error", "errorMessage", "error_message"]) ?? null,
  };
}

function normalizeExecutiveMemoListItem(data: any): ExecutiveMemoListItem {
  const id = pickStringByPaths(data, ["id", "memoId", "memo_id", "executiveMemoId", "executive_memo_id"]) ?? "";
  const summary = pickStringByPaths(data, ["summary"]) ?? null;
  const bodyPreview =
    pickStringByPaths(data, ["bodyPreview", "body_preview", "preview"]) ??
    summary ??
    pickStringByPaths(data, ["body"])?.slice(0, 160) ??
    "";

  return {
    id,
    title: pickStringByPaths(data, ["title"]) ?? null,
    bodyPreview,
    summary,
    kind: pickStringByPaths(data, ["kind"]) ?? null,
    themes: pickStringArrayOrDelimitedByPaths(data, ["themes", "theme"]) ?? [],
    tags: pickStringArrayOrDelimitedByPaths(data, ["tags", "tag"]) ?? [],
    priority: normalizeExecutiveMemoPriority(pickStringByPaths(data, ["priority"])),
    capturedAt: pickStringByPaths(data, ["capturedAt", "captured_at"]) ?? "",
    status: normalizeExecutiveMemoStatus(pickStringByPaths(data, ["status"])),
    updatedAt: pickStringByPaths(data, ["updatedAt", "updated_at"]) ?? null,
  };
}

function normalizeExecutiveMemoDetail(data: any): ExecutiveMemoDetailData {
  const listItem = normalizeExecutiveMemoListItem(data);
  const sourceImage = getByPath(data, "sourceImage") ?? getByPath(data, "source_image") ?? {};

  return {
    ...listItem,
    body: pickStringByPaths(data, ["body"]) ?? "",
    ocrRawText: pickStringByPaths(data, ["ocrRawText", "ocr_raw_text"]) ?? null,
    actionItems: normalizeExecutiveMemoActionItems(
      getByPath(data, "actionItems") ?? getByPath(data, "action_items")
    ),
    relatedAgents: pickStringArrayOrDelimitedByPaths(data, ["relatedAgents", "related_agents"]) ?? [],
    sourceImage: {
      available:
        pickBooleanByPaths(sourceImage, ["available", "isAvailable", "is_available"]) ??
        false,
      expiresAt: pickStringByPaths(sourceImage, ["expiresAt", "expires_at"]) ?? null,
    },
    version: pickNumberByPaths(data, ["version"]) ?? 0,
    createdAt: pickStringByPaths(data, ["createdAt", "created_at"]) ?? "",
    updatedAt: pickStringByPaths(data, ["updatedAt", "updated_at"]) ?? "",
  };
}

function normalizeReceiptDetailData(data: any): ReceiptDetailData {
  const normalized = { ...data, relayStatus: data.relayStatus ?? data.status };
  if (normalized.structuredResult) {
    const r = normalized.structuredResult;
    normalized.structuredResult = {
      merchantName: r.merchantName ?? r.merchant_name ?? "",
      receiptDate: r.receiptDate ?? r.receipt_date ?? "",
      totalAmount: r.totalAmount ?? r.total_amount ?? "",
      currency: r.currency ?? "",
      taxAmount: r.taxAmount ?? r.tax_amount ?? "",
      paymentMethod: r.paymentMethod ?? r.payment_method ?? "",
      invoiceNumber: r.invoiceNumber ?? r.invoice_number ?? "",
      accountCategory: r.accountCategory ?? r.account_category ?? "",
      summary: r.summary ?? "",
    };
  }
  return normalized as ReceiptDetailData;
}

function normalizeGoogleIntegrationStatusData(data: any): GoogleIntegrationStatusData {
  const connectionStatus =
    pickStringByPaths(data, [
      "connectionStatus",
      "connection_status",
      "google.connectionStatus",
      "google.connection_status",
    ]) ?? "";
  const scopeBundle =
    pickStringByPaths(data, ["scopeBundle", "scope_bundle", "google.scopeBundle", "google.scope_bundle"]) ?? "";
  const missingScopes = pickStringArrayByPaths(data, [
    "missingScopes",
    "missing_scopes",
    "google.missingScopes",
    "google.missing_scopes",
  ]);
  const scopeBundleGranted =
    pickBooleanByPaths(data, [
      "scopeBundleGranted",
      "scope_bundle_granted",
      "google.scopeBundleGranted",
      "google.scope_bundle_granted",
    ]) ?? false;
  const isConnected =
    pickBooleanByPaths(data, ["isConnected", "is_connected", "connected", "google.isConnected"]) ??
    connectionStatus.toLowerCase() !== "not_connected";
  const email = pickStringByPaths(data, ["email", "google.email"]);
  const connectedAt = pickStringByPaths(data, ["connectedAt", "connected_at", "google.connectedAt", "google.connected_at"]);
  const lastError = pickStringByPaths(data, ["lastError", "last_error", "google.lastError", "google.last_error"]);

  if (!scopeBundle) {
    throw new AppError("Invalid /integrations/google/status response.", "INVALID_RESPONSE");
  }

  return {
    connectionStatus,
    isConnected,
    scopeBundle,
    scopeBundleGranted,
    missingScopes,
    email,
    connectedAt,
    lastError: lastError ?? null,
  };
}

function parseJsonSafely(text?: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function normalizeGoogleIntegrationStatusConflictFallback(
  error: unknown
): GoogleIntegrationStatusData | null {
  if (!(error instanceof AppError) || error.code !== "CONFLICT") {
    return null;
  }

  const parsed = parseJsonSafely(error.meta?.responseText);
  const dataCandidate =
    parsed && typeof parsed === "object" && "data" in (parsed as Record<string, unknown>)
      ? (parsed as { data?: unknown }).data
      : parsed;

  if (dataCandidate && typeof dataCandidate === "object") {
    try {
      return normalizeGoogleIntegrationStatusData(dataCandidate);
    } catch {
      // Ignore invalid payload.
    }
  }

  return null;
}

function createGoogleIntegrationStatusNotConnectedFallback(
  scopeBundle: string,
  error: AppError
): GoogleIntegrationStatusData {
  return {
    connectionStatus: "not_connected",
    isConnected: false,
    scopeBundle,
    scopeBundleGranted: false,
    missingScopes: [],
    lastError: error.message ?? null,
  };
}

function buildGoogleIntegrationStatusPath(scopeBundle: string) {
  return buildPath(`/integrations/google/status${buildQuery({ scopeBundle })}`);
}

function normalizeGoogleIntegrationConnectData(data: any): GoogleIntegrationConnectData {
  const scopeBundle =
    pickStringByPaths(data, ["scopeBundle", "scope_bundle", "google.scopeBundle", "google.scope_bundle"]) ?? "";
  const authorizationUrl =
    pickStringByPaths(data, [
      "authorizationUrl",
      "authorization_url",
      "google.authorizationUrl",
      "google.authorization_url",
    ]) ?? "";
  const state = pickStringByPaths(data, ["state", "google.state"]) ?? "";
  const expiresInSeconds =
    pickNumberByPaths(data, ["expiresInSeconds", "expires_in_seconds", "google.expiresInSeconds", "google.expires_in_seconds"]) ??
    0;
  const resolvedScopeBundle = pickStringByPaths(data, [
    "resolvedScopeBundle",
    "resolved_scope_bundle",
    "google.resolvedScopeBundle",
    "google.resolved_scope_bundle",
  ]);
  const resolvedScopes = pickStringArrayByPaths(data, [
    "resolvedScopes",
    "resolved_scopes",
    "google.resolvedScopes",
    "google.resolved_scopes",
  ]);

  if (!scopeBundle || !authorizationUrl) {
    throw new AppError("Invalid /integrations/google/connect response.", "INVALID_RESPONSE");
  }

  return {
    scopeBundle,
    authorizationUrl,
    state,
    expiresInSeconds,
    resolvedScopeBundle,
    resolvedScopes,
  };
}

function normalizeLineOAuthAuthorizeData(data: any, path: string): LineOAuthAuthorizeData {
  const authorizeUrl =
    pickStringByPaths(data, [
      "authorize_url",
      "authorizeUrl",
      "authorization_url",
      "authorizationUrl",
      "url",
      "line.authorize_url",
      "line.authorizeUrl",
      "line.authorization_url",
      "line.authorizationUrl",
      "line.url",
      "data.authorize_url",
      "data.authorizeUrl",
      "data.authorization_url",
      "data.authorizationUrl",
      "data.url",
    ]) ?? "";
  const state = pickStringByPaths(data, ["state", "line.state", "data.state"]) ?? "";
  const expiresAt =
    pickStringByPaths(data, [
      "expires_at",
      "expiresAt",
      "line.expires_at",
      "line.expiresAt",
      "data.expires_at",
      "data.expiresAt",
    ]) ?? null;

  if (!authorizeUrl || !state) {
    throw new AppError("Invalid /line/oauth/authorize response.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(path),
      method: "POST",
      responseText: safeText(JSON.stringify(data)),
    });
  }

  return {
    authorizeUrl,
    state,
    expiresAt,
  };
}

function isDisconnectedStatusValue(value?: string): boolean {
  if (!value) return false;

  return ["revoked", "disconnected", "inactive", "unlinked"].includes(value.trim().toLowerCase());
}

function isFormDataBody(body: RequestInit["body"]) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function buildRequestHeaders(options: RequestInit): Record<string, string> {
  const deviceParams = getDeviceParams();

  return {
    ...(isFormDataBody(options.body) ? {} : { "Content-Type": "application/json" }),
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "X-Terminal-Identification-Number": deviceParams.terminalIdentificationNumber,
    ...(options.headers as any),
  };
}

function getResponseMeta(res: Response) {
  return {
    responseUrl: safeText(res.url ?? ""),
    responseContentType: safeText(res.headers?.get("content-type") ?? ""),
    location: safeText(res.headers?.get("location") ?? ""),
    redirected: res.redirected,
  };
}

function isLikelyHtmlResponse(res: Response, text: string): boolean {
  const contentType = (res.headers?.get("content-type") ?? "").toLowerCase();
  if (contentType.includes("text/html")) return true;

  const normalized = text.trimStart().toLowerCase();
  return normalized.startsWith("<!doctype html") || normalized.startsWith("<html");
}

function uniqStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

function buildApiPrefixFallbackEndpoints(endpoint: string): string[] {
  const fallbacks: string[] = [];

  if (endpoint.startsWith("/api/")) {
    fallbacks.push(endpoint.slice(4));

    if (typeof window !== "undefined" && window.location?.origin) {
      fallbacks.push(`${window.location.origin}${endpoint}`);
      fallbacks.push(`${window.location.origin}${endpoint.slice(4)}`);
    }

    for (const host of API_FALLBACK_HOSTS) {
      fallbacks.push(`${host}${endpoint}`);
      fallbacks.push(`${host}${endpoint.slice(4)}`);
    }

    return uniqStrings(fallbacks);
  }

  if (endpoint.startsWith("/v1/")) {
    fallbacks.push(`/api${endpoint}`);

    if (typeof window !== "undefined" && window.location?.origin) {
      fallbacks.push(`${window.location.origin}/api${endpoint}`);
    }

    for (const host of API_FALLBACK_HOSTS) {
      fallbacks.push(`${host}/api${endpoint}`);
    }

    return uniqStrings(fallbacks);
  }

  if (!endpoint.startsWith("http://") && !endpoint.startsWith("https://")) {
    return [];
  }

  try {
    const parsed = new URL(endpoint);
    if (!parsed.pathname.startsWith("/api/") && !parsed.pathname.startsWith("/v1/")) {
      return [];
    }

    if (parsed.pathname.startsWith("/api/")) {
      const withoutApiPrefix = new URL(parsed.toString());
      withoutApiPrefix.pathname = withoutApiPrefix.pathname.slice(4);
      fallbacks.push(withoutApiPrefix.toString());

      for (const host of API_FALLBACK_HOSTS) {
        fallbacks.push(`${host}${parsed.pathname}${parsed.search}`);
        fallbacks.push(`${host}${parsed.pathname.slice(4)}${parsed.search}`);
      }
    }

    if (parsed.pathname.startsWith("/v1/")) {
      const withApiPrefix = new URL(parsed.toString());
      withApiPrefix.pathname = `/api${withApiPrefix.pathname}`;
      fallbacks.push(withApiPrefix.toString());

      for (const host of API_FALLBACK_HOSTS) {
        fallbacks.push(`${host}/api${parsed.pathname}${parsed.search}`);
      }
    }

    return uniqStrings(fallbacks);
  } catch {
    return [];
  }
}

async function requestJson<T>(fullPath: string, options: RequestInit): Promise<T> {
  const method = (options.method ?? "GET").toString();
  const endpoint = buildEndpoint(fullPath);
  const fallbackAttempts: Array<{
    endpoint: string;
    status?: number;
    contentType?: string;
    result: "success" | "non_json" | "http_error" | "network_error";
  }> = [];

  let res: Response;
  let text = "";

  try {
    const headers = buildRequestHeaders(options);

    res = await fetch(endpoint, { ...options, headers });
    text = await res.text().catch(() => "");

    if (!res.ok) {
      throw new AppError(`API request failed: ${res.status} ${res.statusText}`, statusToCode(res.status), {
        endpoint,
        method,
        status: res.status,
        responseText: safeText(text),
        ...getResponseMeta(res),
      });
    }

    if (res.status === 204 || text === "") {
      return null as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      const fallbackEndpoints = buildApiPrefixFallbackEndpoints(endpoint);
      if (fallbackEndpoints.length > 0 && isLikelyHtmlResponse(res, text)) {
        for (const fallbackEndpoint of fallbackEndpoints) {
          let fallbackRes: Response;
          let fallbackText = "";

          try {
            fallbackRes = await fetch(fallbackEndpoint, { ...options, headers });
            fallbackText = await fallbackRes.text().catch(() => "");

            if (!fallbackRes.ok) {
              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "http_error",
              });
              continue;
            }

            try {
              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "success",
              });
              sentryCaptureMessage("API fallback used", "warning", {
                primaryEndpoint: endpoint,
                fallbackEndpoint,
                method,
                primaryStatus: res.status,
              });
              return JSON.parse(fallbackText) as T;
            } catch {
              if (isLikelyHtmlResponse(fallbackRes, fallbackText)) {
                fallbackAttempts.push({
                  endpoint: fallbackEndpoint,
                  status: fallbackRes.status,
                  contentType: fallbackRes.headers?.get("content-type") ?? "",
                  result: "non_json",
                });
                continue;
              }

              fallbackAttempts.push({
                endpoint: fallbackEndpoint,
                status: fallbackRes.status,
                contentType: fallbackRes.headers?.get("content-type") ?? "",
                result: "non_json",
              });
            }
          } catch (fallbackError) {
            fallbackAttempts.push({
              endpoint: fallbackEndpoint,
              result: "network_error",
            });
          }
        }

        const fallback403 = fallbackAttempts.find((attempt) => attempt.result === "http_error" && attempt.status === 403);
        if (fallback403) {
          throw new AppError("API request failed: 403 Forbidden", "FORBIDDEN", {
            endpoint: fallback403.endpoint,
            method,
            status: 403,
            responseText: safeText(text),
            fallbackFromEndpoint: endpoint,
            fallbackAttempts,
          });
        }

        const fallback401 = fallbackAttempts.find((attempt) => attempt.result === "http_error" && attempt.status === 401);
        if (fallback401) {
          throw new AppError("API request failed: 401 Unauthorized", "UNAUTHORIZED", {
            endpoint: fallback401.endpoint,
            method,
            status: 401,
            responseText: safeText(text),
            fallbackFromEndpoint: endpoint,
            fallbackAttempts,
          });
        }
      }

      const missingRecordsEndpoint =
        isCompanyResearchRecordsPath(fullPath) && fallbackAttempts.some((attempt) => attempt.status === 404);
      if (!missingRecordsEndpoint) {
        console.error("[api] non-JSON response", {
          endpoint,
          method,
          status: res.status,
          text: safeText(text),
          fallbackAttempts,
        });
      }
      throw new AppError("API returned non-JSON response.", "INVALID_RESPONSE", {
        endpoint,
        method,
        status: res.status,
        responseText: safeText(text),
        fallbackAttempts,
        ...getResponseMeta(res),
      });
    }
  } catch (e: any) {
    if (e instanceof AppError) throw e;
    throw new AppError("Network error.", "NETWORK", {
      endpoint,
      method,
      causeName: e?.name,
      responseText: safeText(String(e?.message ?? e ?? "")),
    });
  }
}

async function requestEnvelopeData<T>(fullPath: string, options: RequestInit): Promise<T> {
  const env = await requestJson<ApiEnvelope<T>>(fullPath, options);
  if (!env || typeof env !== "object" || env.data === undefined) {
    console.error("[api] missing envelope data", { endpoint: buildEndpoint(fullPath), method: options.method ?? "GET", env });
    throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(fullPath),
      method: (options.method ?? "GET").toString(),
      responseText: safeText(JSON.stringify(env)),
    });
  }
  return env.data as T;
}

async function requestEnvelopeDataMultipart<T>(fullPath: string, options: RequestInit): Promise<T> {
  const env = await requestJson<ApiEnvelope<T>>(fullPath, options);
  if (!env || typeof env !== "object" || env.data === undefined) {
    throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(fullPath),
      method: (options.method ?? "GET").toString(),
      responseText: safeText(JSON.stringify(env)),
    });
  }
  return env.data as T;
}

function isEndpointMissing(error: unknown) {
  if (!(error instanceof AppError)) return false;
  if (error.code === "NOT_FOUND" || error.meta.status === 404) return true;
  return Boolean(error.meta.fallbackAttempts?.some((attempt) => attempt.status === 404));
}

function isCompanyResearchRecordsPath(fullPath: string) {
  return fullPath.includes("/agents/company-research/records");
}

async function requestLineRegisterEnvelope<T>(fullPath: string, options: RequestInit): Promise<T> {
  const env = await requestJson<ApiEnvelope<any>>(fullPath, options);
  const code = Number(env?.code);

  // Omit data field from error meta to avoid leaking tokens in logs
  const safeEnvText = safeText(JSON.stringify({ code: env?.code, message: env?.message }));

  if (code !== 200) {
    const backendMessage = typeof env?.message === "string" ? env.message : "";
    const errorCode =
      code === 400 ? "BAD_REQUEST"
      : code === 401 ? "UNAUTHORIZED"
      : code === 403 ? "FORBIDDEN"
      : code >= 500 ? "SERVER"
      : "UNKNOWN";
    throw new AppError(
      backendMessage || "LINE registration error.",
      errorCode,
      {
        endpoint: buildEndpoint(fullPath),
        method: (options.method ?? "GET").toString(),
        responseText: safeEnvText,
      }
    );
  }

  if (!env || typeof env !== "object" || env.data === undefined) {
    throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
      endpoint: buildEndpoint(fullPath),
      method: (options.method ?? "GET").toString(),
      responseText: safeEnvText,
    });
  }

  return env.data as T;
}

function normalizeNewsReportSubscriptionMode(value: unknown): NewsReportSubscriptionMode {
  return value === "report" ? "report" : "news";
}

function normalizeNewsReportSubscriptionFrequency(value: unknown): NewsReportSubscriptionFrequency {
  if (value === "weekly" || value === "monthly") return value;
  return "daily";
}

function normalizeNewsReportSubscriptionStatus(value: unknown): NewsReportSubscriptionStatus {
  if (value === "paused" || value === "disabled") return value;
  return "active";
}

function normalizeNewsReportSubscriptionNotificationType(value: unknown): NewsReportSubscriptionNotificationType {
  if (value === "email" || value === "google_chat") return value;
  return "line";
}

function normalizeNewsReportSubscriptionDeliveryFormat(value: unknown): NewsReportSubscriptionDeliveryFormat {
  return value === "text_and_audio" ? "text_and_audio" : "text";
}

function normalizeNumberList(value: unknown, min: number, max: number): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item >= min && item <= max);
}

function normalizeNewsReportScheduleConfig(value: unknown): NewsReportSubscriptionScheduleConfig | null {
  if (!isPlainRecord(value)) return null;

  if (value.type === "daily") {
    return { type: "daily" };
  }

  if (value.type === "weekly") {
    const daysOfWeek = normalizeNumberList(value.days_of_week, 1, 7);
    return { type: "weekly", days_of_week: daysOfWeek.length > 0 ? daysOfWeek : [1] };
  }

  if (value.type === "monthly") {
    if (value.monthly_mode === "last_day") {
      return { type: "monthly", monthly_mode: "last_day" };
    }

    const daysOfMonth = normalizeNumberList(value.days_of_month, 1, 28);
    return {
      type: "monthly",
      monthly_mode: "day_of_month",
      days_of_month: daysOfMonth.length > 0 ? daysOfMonth : [1],
    };
  }

  return null;
}

function normalizeNewsReportSubscription(data: unknown): NewsReportSubscription {
  if (!isPlainRecord(data)) {
    throw new AppError("Invalid /news/subscriptions response.", "INVALID_RESPONSE");
  }

  const id = pickStringByPaths(data, ["id", "subscriptionId", "subscription_id"]);
  const keyword = pickStringByPaths(data, ["keyword", "query", "theme"]);
  const deliveryTime = pickStringByPaths(data, ["deliveryTime", "delivery_time"]);
  const timezone = pickStringByPaths(data, ["timezone"]);

  if (!id || !keyword || !deliveryTime || !timezone) {
    throw new AppError("Invalid /news/subscriptions response.", "INVALID_RESPONSE", {
      responseText: safeText(JSON.stringify(data)),
    });
  }

  return {
    id,
    mode: normalizeNewsReportSubscriptionMode(pickStringByPaths(data, ["mode", "type"])),
    title: pickStringByPaths(data, ["title"]) ?? null,
    keyword,
    frequency: normalizeNewsReportSubscriptionFrequency(pickStringByPaths(data, ["frequency"])),
    scheduleConfig: normalizeNewsReportScheduleConfig(
      getByPath(data, "scheduleConfig") ?? getByPath(data, "schedule_config")
    ),
    deliveryTime,
    timezone,
    notificationType: normalizeNewsReportSubscriptionNotificationType(
      pickStringByPaths(data, ["notificationType", "notification_type"])
    ),
    email: pickStringByPaths(data, ["email"]) ?? null,
    emailMasked: pickStringByPaths(data, ["emailMasked", "email_masked"]) ?? null,
    deliveryFormat: normalizeNewsReportSubscriptionDeliveryFormat(
      pickStringByPaths(data, ["deliveryFormat", "delivery_format"])
    ),
    readingProfile: pickStringByPaths(data, ["readingProfile", "reading_profile"]) ?? null,
    status: normalizeNewsReportSubscriptionStatus(pickStringByPaths(data, ["status"])),
    nextRunAt: pickStringByPaths(data, ["nextRunAt", "next_run_at"]) ?? null,
    lastRunAt: pickStringByPaths(data, ["lastRunAt", "last_run_at"]) ?? null,
    createdAt: pickStringByPaths(data, ["createdAt", "created_at"]) ?? null,
    updatedAt: pickStringByPaths(data, ["updatedAt", "updated_at"]) ?? null,
  };
}

function normalizeNewsReportSubscriptionList(data: unknown): NewsReportSubscriptionList {
  const rawItems = Array.isArray(data)
    ? data
    : isPlainRecord(data) && Array.isArray(data.items)
    ? data.items
    : isPlainRecord(data) && Array.isArray(data.subscriptions)
    ? data.subscriptions
    : [];

  return {
    items: rawItems.map((item) => normalizeNewsReportSubscription(item)),
    nextCursor: isPlainRecord(data) ? pickStringByPaths(data, ["nextCursor", "next_cursor"]) ?? null : null,
  };
}

function normalizeNewsReportSubscriptionRunNowResponse(data: unknown): NewsReportSubscriptionRunNowResponse {
  if (!isPlainRecord(data)) {
    throw new AppError("Invalid /news/subscriptions/{id}/run-now response.", "INVALID_RESPONSE");
  }

  const runId = pickStringByPaths(data, ["runId", "run_id"]);
  const status = pickStringByPaths(data, ["status"]);

  if (!runId || !status) {
    throw new AppError("Invalid /news/subscriptions/{id}/run-now response.", "INVALID_RESPONSE", {
      responseText: safeText(JSON.stringify(data)),
    });
  }

  return {
    runId,
    status,
    agentJobId: pickStringByPaths(data, ["agentJobId", "agent_job_id"]) ?? null,
  };
}

let authRefreshPromise: Promise<AuthSession> | null = null;
let refreshTokenPromise: Promise<AuthSession> | null = null;

async function runPostLoginInitialization(params: {
  accessToken: string;
}): Promise<void> {
  const deviceParams = getDeviceParams();
  const qs = buildQuery({
    terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
  });
  const path = buildPath(`/event/login${qs}`);

  try {
    await requestEnvelopeData<EventLoginData>(path, {
      method: "GET",
      headers: { Authorization: `Bearer ${params.accessToken}` },
    });
  } catch (e) {
    if (e instanceof AppError && (e.code === "UNAUTHORIZED" || e.code === "FORBIDDEN")) {
      throw e;
    }
  }
}

async function refreshStoredAuthSession(params: {
  userId: string;
  secretKey: string;
}): Promise<AuthSession> {
  if (!authRefreshPromise) {
    authRefreshPromise = (async () => {
      const loggedIn = await api.login({
        userId: params.userId,
        secretKey: params.secretKey,
      });
      const accessToken = loggedIn.accessToken;

      storage.setAuth({ accessToken });
      await runPostLoginInitialization({ accessToken });

      return {
        userId: params.userId,
        secretKey: params.secretKey,
        accessToken,
      };
    })().finally(() => {
      authRefreshPromise = null;
    });
  }

  return await authRefreshPromise;
}

async function refreshStoredAuthByRefreshToken(params: {
  refreshToken: string;
  terminalIdentificationNumber: string;
}): Promise<AuthSession> {
  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      const result = await api.refreshAuth(params);
      storage.setAuth({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      const userId = storage.getAuth().userId ?? "";
      return { userId, accessToken: result.accessToken };
    })().finally(() => {
      refreshTokenPromise = null;
    });
  }
  return await refreshTokenPromise;
}

async function withAuthRetry<T>(fn: (accessToken: string) => Promise<T>): Promise<T> {
  const auth = await api.ensureAuth();
  try {
    return await fn(auth.accessToken);
  } catch (e: any) {
    if (e instanceof AppError && e.code === "UNAUTHORIZED") {
      storage.setAuth({ accessToken: undefined });
      const refreshed = await api.ensureAuth();
      return await fn(refreshed.accessToken);
    }
    throw e;
  }
}

function normalizeChatHistoryData(data: ChatHistoryData | null | undefined): ChatHistoryResult {
  const histories = data?.histories;
  return {
    histories: Array.isArray(histories) ? histories : [],
    hasMore: (data?.has_more ?? data?.hasMore) === true,
    nextBeforeSeq: pickNumberByPaths(data ?? {}, ["nextBeforeSeq", "next_before_seq"]) ?? null,
  };
}

function isGroupTalkReportStatus(value: unknown): value is GroupTalkReportStatus {
  return value === "not_generated" || value === "generating" || value === "completed" || value === "failed";
}

function isGroupTalkReportResponse(value: unknown): value is GroupTalkReportResponse {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<GroupTalkReportResponse>;
  return (
    isGroupTalkReportStatus(item.status) &&
    (item.title === undefined || typeof item.title === "string") &&
    (item.markdown_body === undefined || typeof item.markdown_body === "string") &&
    (item.generated_at === undefined || typeof item.generated_at === "string") &&
    (item.source_hash === undefined || typeof item.source_hash === "string") &&
    (item.error_message === undefined || typeof item.error_message === "string") &&
    (item.retryable === undefined || typeof item.retryable === "boolean") &&
    (item.is_stale === undefined || typeof item.is_stale === "boolean") &&
    (item.can_regenerate === undefined || typeof item.can_regenerate === "boolean")
  );
}

// ====== API ======
export const api = {
  async createUser(): Promise<{ userId: string; secretKey: string }> {
    const deviceParams = getDeviceParams();

    const path = buildPath("/user");
    const requestCreateUser = async (includeDeviceParams: boolean) => {
      return await requestEnvelopeData<CreateUserData>(path, {
        method: "POST",
        ...(includeDeviceParams ? { body: JSON.stringify(deviceParams) } : {}),
      });
    };

    let data: CreateUserData;
    try {
      // 現行バックエンド: /user は device params を要求
      data = await requestCreateUser(true);
    } catch (error) {
      // 互換対応: body なし実装のバックエンド向け
      if (!(error instanceof AppError) || error.code !== "BAD_REQUEST") {
        throw error;
      }
      data = await requestCreateUser(false);
    }

    const userId = data.id;
    const secretKey = data.secretKey;

    if (!userId || !secretKey) {
      throw new AppError("Invalid /user response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify({ id: data.id })),
      });
    }

    return { userId, secretKey };
  },

  async login(params: { userId: string; secretKey: string }): Promise<{ accessToken: string }> {
    const path = buildPath("/login");
    const data = await requestEnvelopeData<LoginData>(path, {
      method: "POST",
      body: JSON.stringify(params),
    });

    if (!data.accessToken) {
      throw new AppError("Invalid /login response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify({})),
      });
    }

    return { accessToken: data.accessToken };
  },

  async emailInviteLogin(params: { email: string; inviteCode: string }): Promise<{
    email: string;
    userId: string;
    accessToken: string;
    refreshToken?: string;
    isNewUser?: boolean;
  }> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/email-invite");
    const data = await requestEnvelopeData<EmailInviteLoginData>(path, {
      method: "POST",
      body: JSON.stringify({
        email: params.email,
        invite_code: params.inviteCode,
        terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
        deviceType: String(deviceParams.deviceType),
      }),
    });

    if (!data.email || !data.userId || !data.accessToken) {
      throw new AppError("Invalid /auth/email-invite response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return {
      email: data.email,
      userId: data.userId,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      isNewUser: data.isNewUser,
    };
  },

  async emailOtpRequest(params: { email: string }): Promise<void> {
    const path = buildPath("/auth/email/otp/request");
    await requestEnvelopeData<Record<string, never>>(path, {
      method: "POST",
      body: JSON.stringify({ email: params.email }),
    });
  },

  async emailOtpVerify(params: {
    email: string;
    code: string;
  }): Promise<{
    email: string;
    userId: string;
    accessToken: string;
    refreshToken?: string;
    isNewUser?: boolean;
  }> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/email/otp/verify");
    const data = await requestEnvelopeData<EmailInviteLoginData>(path, {
      method: "POST",
      body: JSON.stringify({
        email: params.email,
        code: params.code,
        terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
        deviceType: String(deviceParams.deviceType),
      }),
    });

    if (!data.email || !data.userId || !data.accessToken) {
      throw new AppError("Invalid /auth/email/otp/verify response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return {
      email: data.email,
      userId: data.userId,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      isNewUser: data.isNewUser,
    };
  },

  async emailOtpResend(params: { email: string }): Promise<void> {
    const path = buildPath("/auth/email/otp/resend");
    await requestEnvelopeData<Record<string, never>>(path, {
      method: "POST",
      body: JSON.stringify({ email: params.email }),
    });
  },

  async getMe(): Promise<AccountState> {
    const path = buildPath("/me");
    const { accessToken } = storage.getAuth();
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
    const data = await requestEnvelopeData<any>(path, {
      method: "GET",
      ...(headers ? { headers } : {}),
    });

    return normalizeAccountState(data);
  },

  async applyInviteCode(inviteCode: string): Promise<InviteApplyResult> {
    const path = buildPath("/invite/apply");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ invite_code: inviteCode }),
      });

      return normalizeInviteApplyResult(data);
    });
  },

  async applyPrivilegeCode(privilegeCode: string): Promise<PrivilegeApplyResult> {
    const path = buildPath("/privilege-codes/apply");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ privilege_code: privilegeCode }),
      });

      return {
        applied: pickBooleanByPaths(data, ["applied"]) ?? false,
        alreadyApplied: pickBooleanByPaths(data, ["alreadyApplied", "already_applied"]) ?? false,
        accountState: normalizeAccountState(data?.accountState ?? data?.account_state),
      };
    });
  },

  async refreshAuth(params: {
    refreshToken: string;
    terminalIdentificationNumber: string;
  }): Promise<{ accessToken: string; refreshToken: string }> {
    const path = buildPath("/auth/refresh");
    const data = await requestEnvelopeData<{ accessToken?: string; refreshToken?: string }>(path, {
      method: "POST",
      body: JSON.stringify({
        refreshToken: params.refreshToken,
        terminalIdentificationNumber: params.terminalIdentificationNumber,
      }),
    });

    if (!data.accessToken || !data.refreshToken) {
      throw new AppError("Invalid /auth/refresh response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  },

  async ensureAuth(): Promise<AuthSession> {
    const current = storage.getAuth();
    let { userId, secretKey, accessToken, refreshToken } = current;

    if (!userId) {
      // userId がない場合のみ新規ユーザー作成（非 SSO フロー）
      const created = await this.createUser();
      userId = created.userId;
      secretKey = created.secretKey;
      storage.setAuth({ userId, secretKey });
    }

    if (!accessToken) {
      if (refreshToken) {
        const device = getDeviceParams();
        try {
          const refreshed = await refreshStoredAuthByRefreshToken({
            refreshToken,
            terminalIdentificationNumber: device.terminalIdentificationNumber,
          });
          accessToken = refreshed.accessToken;
        } catch {
          storage.setAuth({ refreshToken: undefined });
          throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
        }
      } else if (secretKey) {
        const refreshed = await refreshStoredAuthSession({ userId, secretKey });
        accessToken = refreshed.accessToken;
      } else {
        throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
      }
    }

    return { userId, secretKey, accessToken };
  },

  async getUser(): Promise<UserData> {
    const deviceParams = getDeviceParams();

    const qs = buildQuery({
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/user${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<UserData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getProfileMaster(): Promise<ProfileMasterItem[]> {
    const path = buildPath("/profile/master");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      return unwrapList<any>(data)
        .map((item) => normalizeProfileMasterItem(item))
        .filter((item): item is ProfileMasterItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async getUserProfile(): Promise<UserProfileItem[]> {
    const path = buildPath("/profile");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      return unwrapList<any>(data)
        .map((item) => normalizeUserProfileItem(item))
        .filter((item): item is UserProfileItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async updateUserProfile(params: { profiles: UserProfileUpdateItem[] }): Promise<UserProfileItem[]> {
    const path = buildPath("/profile");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profiles: params.profiles.map((item) => ({
            key: item.key,
            value: item.value,
          })),
        }),
      });

      return unwrapList<any>(data)
        .map((item) => normalizeUserProfileItem(item))
        .filter((item): item is UserProfileItem => item !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
    });
  },

  async eventLogin(params?: { terminalIdentificationNumber?: string }): Promise<EventLoginData> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const terminalIdentificationNumber =
      params?.terminalIdentificationNumber ?? deviceParams.terminalIdentificationNumber;

    const qs = buildQuery({ terminalIdentificationNumber });
    const path = buildPath(`/event/login${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<EventLoginData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async startLineOAuth(params: { redirectUri: string }): Promise<LineOAuthAuthorizeData> {
    const path = buildPath("/line/oauth/authorize");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          redirect_uri: params.redirectUri,
        }),
      });

      return normalizeLineOAuthAuthorizeData(data, path);
    });
  },

  async completeLineOAuth(params: {
    code: string;
    state: string;
    redirectUri: string;
  }): Promise<LineOAuthCallbackData> {
    const path = buildPath("/line/oauth/callback");

    return await withAuthRetry(async (accessToken) => {
      const env = await requestJson<ApiEnvelope<any>>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri,
        }),
      });

      if (Number(env.code) === 403) {
        throw new AppError("Not a LINE friend or blocked.", "FORBIDDEN", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(env)),
        });
      }

      if (!env || typeof env !== "object" || env.data === undefined) {
        throw new AppError("Missing envelope data.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(env)),
        });
      }

      const data = env.data;

      const status = pickStringByPaths(data, [
        "status",
        "lineStatus",
        "line_link_status",
        "connectionStatus",
        "connection_status",
        "line.status",
      ]);
      const isLinked = pickBooleanByPaths(data, [
        "isLinked",
        "is_linked",
        "lineLinked",
        "line_linked",
        "connected",
        "line.isLinked",
      ]);
      const notificationUserId = pickStringByPaths(data, [
        "notificationUserId",
        "notification_user_id",
        "lineUserId",
        "line_user_id",
        "line.notificationUserId",
        "line.userId",
      ]);
      const lineDisplayName = pickStringByPaths(data, [
        "lineDisplayName",
        "line_display_name",
        "displayName",
        "display_name",
        "line.lineDisplayName",
      ]);
      const linkedAt = pickStringByPaths(data, [
        "linkedAt",
        "linked_at",
        "connectedAt",
        "line.linkedAt",
      ]);
      const friendshipStatusChanged = pickBooleanByPaths(data, [
        "friendshipStatusChanged",
        "friendship_status_changed",
        "line.friendshipStatusChanged",
      ]);

      if (!status && isLinked === undefined && !notificationUserId) {
        throw new AppError("Invalid /line/oauth/callback response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return {
        status,
        isLinked,
        notificationUserId,
        lineDisplayName,
        linkedAt,
        friendshipStatusChanged,
      };
    });
  },

  async createLineLinkCode(): Promise<LineLinkCodeData> {
    const path = buildPath("/line/link-code");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<LineLinkCodeData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!data?.link_code || !data?.expires_at) {
        throw new AppError("Invalid /line/link-code response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return {
        link_code: data.link_code,
        expires_at: data.expires_at,
        instruction:
          data.instruction || `LINE公式アカウントに「連携 ${data.link_code}」と送信してください。`,
      };
    });
  },

  async getLineLinkStatus(): Promise<LineLinkStatusData> {
    const path = buildPath("/line/status");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const status = pickStringByPaths(data, [
        "status",
        "lineStatus",
        "line_link_status",
        "connectionStatus",
        "connection_status",
        "line.status",
      ]);
      const isLinked = pickBooleanByPaths(data, [
        "isLinked",
        "is_linked",
        "lineLinked",
        "line_linked",
        "connected",
        "line.isLinked",
      ]);
      const notificationUserId = pickStringByPaths(data, [
        "notificationUserId",
        "notification_user_id",
        "lineUserId",
        "line_user_id",
        "line.notificationUserId",
        "line.userId",
      ]);
      const lineDisplayName = pickStringByPaths(data, [
        "lineDisplayName",
        "line_display_name",
        "displayName",
        "display_name",
        "line.lineDisplayName",
      ]);
      const linkedAt = pickStringByPaths(data, [
        "linkedAt",
        "linked_at",
        "connectedAt",
        "line.linkedAt",
      ]);

      if (!status && isLinked === undefined && !notificationUserId && !lineDisplayName) {
        throw new AppError("Invalid /line/status response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { status, isLinked, notificationUserId, lineDisplayName, linkedAt };
    });
  },

  async disconnectLineLink(): Promise<LineLinkDisconnectData> {
    const path = buildPath("/line/link");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<LineLinkDisconnectData>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!data || typeof data.disconnected !== "boolean") {
        throw new AppError("Invalid /line/link response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "DELETE",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async updateUserSelection(params: { characterId?: string; voiceId?: string }): Promise<void> {
    const deviceParams = getDeviceParams();
    const { userId } = await this.ensureAuth();

    if (!params.characterId && !params.voiceId) {
      throw new AppError("updateUserSelection requires characterId or voiceId.", "BAD_REQUEST", {
        endpoint: buildEndpoint(buildPath(`/user/${encodeURIComponent(userId)}`)),
        method: "POST",
      });
    }

    const body: Record<string, any> = {
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    };
    if (params.characterId) body.characterId = params.characterId;
    if (params.voiceId) body.voiceId = params.voiceId;

    const path = buildPath(`/user/${encodeURIComponent(userId)}`);

    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
    });
  },

  async getCharacterList(): Promise<CharacterData[]> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const qs = buildQuery({
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/character${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (Array.isArray(data)) return data as CharacterData[];
      if (data && Array.isArray(data.characters)) return data.characters as CharacterData[];
      return unwrapList<CharacterData>(data);
    });
  },

  async getVoiceList(): Promise<VoiceData[]> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const qs = buildQuery({
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
    });

    const path = buildPath(`/voice${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return unwrapList<VoiceData>(data);
    });
  },

  async getChatHistory(params?: { limit?: number; beforeSeq?: number }): Promise<ChatHistoryResult> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const fetchHistory = async (
      accessToken: string,
      includeTerminalQuery: boolean
    ): Promise<ChatHistoryResult> => {
      const qs = buildQuery({
        terminalIdentificationNumber: includeTerminalQuery
          ? deviceParams.terminalIdentificationNumber
          : undefined,
        limit: params?.limit ?? 30,
        before_seq: params?.beforeSeq,
      });

      const path = buildPath(`/chat/history${qs}`);
      const data = await requestEnvelopeData<ChatHistoryData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeChatHistoryData(data);
    };

    return await withAuthRetry(async (accessToken) => {
      try {
        const result = await fetchHistory(accessToken, true);
        if (
          params?.beforeSeq !== undefined ||
          result.histories.length > 0 ||
          result.hasMore ||
          result.nextBeforeSeq !== null
        ) {
          return result;
        }
      } catch (error) {
        if (
          !(error instanceof AppError) ||
          (error.code !== "UNAUTHORIZED" && error.code !== "BAD_REQUEST" && error.code !== "NOT_FOUND")
        ) {
          throw error;
        }
      }

      try {
        return await fetchHistory(accessToken, false);
      } catch (error) {
        if (
          error instanceof AppError &&
          (error.code === "BAD_REQUEST" || error.code === "NOT_FOUND")
        ) {
          return { histories: [], hasMore: false, nextBeforeSeq: null };
        }
        throw error;
      }
    });
  },

  async getChatModes(params?: { characterId?: string }): Promise<ChatModesData> {
    await this.ensureAuth();
    const qs = buildQuery({
      characterId: params?.characterId,
    });
    const path = buildPath(`/chat/modes${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const modesSource = Array.isArray(data?.modes) ? data.modes : [];
      const modes = modesSource
        .map((mode: any) => normalizeChatModeItem(mode))
        .filter((mode: ChatModeItem | null): mode is ChatModeItem => mode !== null)
        .sort((left: ChatModeItem, right: ChatModeItem) => left.mode - right.mode);

      if (modes.length === 0) {
        throw new AppError("Invalid /chat/modes response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { modes };
    });
  },

  async chatTalk(params: {
    text: string;
    speechType?: number;
    chatMode?: ChatModeValue;
  }): Promise<ChatTalkResponseData> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const body = {
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
      text: params.text,
      speechType: params.speechType ?? 8,
      chatMode: params.chatMode ?? 0,
    };

    const path = buildPath("/wizmate/chat/talk");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ChatTalkResponseData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
    });
  },

  async chatReceipt(params: { file: File }): Promise<any> {
    const deviceParams = getDeviceParams();
    await this.ensureAuth();

    const form = new FormData();
    form.append("terminalIdentificationNumber", deviceParams.terminalIdentificationNumber);
    form.append("file", params.file, params.file.name);

    const path = buildPath("/chat/receipt");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeDataMultipart<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
    });
  },

  async startGoogleIntegration(params: {
    scopeBundle: string;
    redirectUrl: string;
  }): Promise<GoogleIntegrationConnectData> {
    const path = buildPath("/integrations/google/connect");
    const payload = {
      scopeBundle: params.scopeBundle,
      redirectUrl: params.redirectUrl,
    };

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload),
      });
      return normalizeGoogleIntegrationConnectData(data);
    });
  },

  async getGoogleIntegrationStatus(scopeBundle: string): Promise<GoogleIntegrationStatusData> {
    const path = buildGoogleIntegrationStatusPath(scopeBundle);

    return await withAuthRetry(async (accessToken) => {
      try {
        const data = await requestEnvelopeData<any>(path, {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        return normalizeGoogleIntegrationStatusData(data);
      } catch (error) {
        const conflictFallback = normalizeGoogleIntegrationStatusConflictFallback(error);
        if (conflictFallback) {
          return conflictFallback;
        }
        if (
          error instanceof AppError &&
          (error.code === "CONFLICT" || error.code === "UNKNOWN")
        ) {
          return createGoogleIntegrationStatusNotConnectedFallback(scopeBundle, error);
        }
        throw error;
      }
    });
  },

  async getGoogleIntegrationStatusByUserScope(params: {
    wizmateUserId: string;
    scopeBundle: string;
  }): Promise<GoogleIntegrationStatusData> {
    const path = buildPath(
      `/integrations/google/status${buildQuery({
        wizmate_user_id: params.wizmateUserId,
        scopeBundle: params.scopeBundle,
      })}`
    );

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeGoogleIntegrationStatusData(data);
    });
  },

  async disconnectGoogleIntegration(): Promise<GoogleIntegrationDisconnectData> {
    const path = buildPath("/integrations/google/connect");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const previousStatus = pickStringByPaths(data, [
        "previousStatus",
        "previous_status",
        "google.previousStatus",
        "google.previous_status",
      ]);
      const connectionStatus = pickStringByPaths(data, [
        "connectionStatus",
        "connection_status",
        "google.connectionStatus",
        "google.connection_status",
      ]);
      const disconnected =
        pickBooleanByPaths(data, ["disconnected", "isDisconnected", "google.disconnected"]) ??
        (isDisconnectedStatusValue(connectionStatus) ? true : undefined);

      if (disconnected === undefined) {
        throw new AppError("Invalid /integrations/google/connect response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "DELETE",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return {
        disconnected,
        previousStatus,
        connectionStatus,
      };
    });
  },

  async createReceiptJob(params: {
    file: File;
    conversationId?: string;
    messageId?: string;
  }): Promise<ReceiptJobCreateData> {
    const form = new FormData();
    form.append("file", params.file, params.file.name);

    if (params.conversationId) {
      form.append("conversationId", params.conversationId);
    }

    if (params.messageId) {
      form.append("messageId", params.messageId);
    }

    const path = buildPath("/receipts");

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeDataMultipart<ReceiptJobCreateData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
    });
  },

  async getReceiptJob(jobId: string): Promise<ReceiptJobStatusData> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ReceiptJobStatusData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getReceiptHistory(params?: {
    page?: number;
    perPage?: number;
    needsReview?: boolean;
    dateFrom?: string;
    dateTo?: string;
    amountMin?: number;
    amountMax?: number;
  }): Promise<ReceiptHistoryList> {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set("page", String(params.page));
    if (params?.perPage !== undefined) query.set("per_page", String(params.perPage));
    if (params?.needsReview !== undefined) query.set("needs_review", String(params.needsReview));
    if (params?.dateFrom) query.set("date_from", params.dateFrom);
    if (params?.dateTo) query.set("date_to", params.dateTo);
    if (params?.amountMin !== undefined) query.set("amount_min", String(params.amountMin));
    if (params?.amountMax !== undefined) query.set("amount_max", String(params.amountMax));
    const qs = query.toString();
    const path = buildPath(`/receipts${qs ? `?${qs}` : ""}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ReceiptHistoryList>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getReceiptDetail(jobId: string): Promise<ReceiptDetailData> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}`);
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeReceiptDetailData(data);
    });
  },

  async updateReceiptJob(jobId: string, fields: Partial<ReceiptStructuredResult> & { userNotes?: string }): Promise<ReceiptDetailData> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}`);
    return await withAuthRetry(async (accessToken) => {
      // バックエンドは structured_result に入れ子にした snake_case を期待する
      const structuredResult: Record<string, string | undefined> = {};
      if (fields.merchantName !== undefined) structuredResult.merchant_name = fields.merchantName;
      if (fields.receiptDate !== undefined) structuredResult.receipt_date = fields.receiptDate;
      if (fields.totalAmount !== undefined) structuredResult.total_amount = fields.totalAmount;
      if (fields.currency !== undefined) structuredResult.currency = fields.currency;
      if (fields.taxAmount !== undefined) structuredResult.tax_amount = fields.taxAmount;
      if (fields.paymentMethod !== undefined) structuredResult.payment_method = fields.paymentMethod;
      if (fields.invoiceNumber !== undefined) structuredResult.invoice_number = fields.invoiceNumber;
      if (fields.accountCategory !== undefined) structuredResult.account_category = fields.accountCategory;
      if (fields.summary !== undefined) structuredResult.summary = fields.summary;
      const body: Record<string, unknown> = { structured_result: structuredResult };
      if (fields.userNotes !== undefined) body.user_notes = fields.userNotes;
      const data = await requestEnvelopeData<any>(path, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return normalizeReceiptDetailData(data);
    });
  },

  async reviewReceiptJob(jobId: string): Promise<ReceiptReviewResult> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}/review`);
    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<ReceiptReviewResult>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async deleteReceiptJob(jobId: string): Promise<void> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}`);
    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<unknown>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getReceiptSourceImage(jobId: string): Promise<ReceiptSourceImageData> {
    const path = buildPath(`/receipts/${encodeURIComponent(jobId)}/source-image`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const url = pickStringByPaths(data, ["url", "sourceUrl", "source_url"]) ?? "";
      const expiresAt = pickStringByPaths(data, ["expiresAt", "expires_at"]) ?? "";

      if (!url) {
        throw new AppError("Invalid receipt source image response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { url, expiresAt };
    });
  },

  async createExecutiveMemoJob(params: {
    image: File;
    clientRequestId: string;
    capturedAt?: string;
    hint?: string;
  }): Promise<ExecutiveMemoJobCreateData> {
    const path = buildPath("/agents/executive-memos/jobs");

    return await withAuthRetry(async (accessToken) => {
      const formData = new FormData();
      formData.append("image", params.image, params.image.name);
      formData.append("client_request_id", params.clientRequestId);

      if (params.capturedAt) {
        formData.append("captured_at", params.capturedAt);
      }

      if (params.hint) {
        formData.append("hint", params.hint);
      }

      const data = await requestEnvelopeDataMultipart<any>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const normalized = normalizeExecutiveMemoJobStatus(data);
      return {
        jobId: normalized.jobId,
        relayStatus: "accepted",
        acceptedAt: normalized.acceptedAt ?? new Date().toISOString(),
        callbackDueAt: normalized.callbackDueAt ?? null,
      };
    });
  },

  async listExecutiveMemoJobs(params?: {
    relayStatus?: ExecutiveMemoRelayStatus[];
    limit?: number;
    cursor?: string;
  }): Promise<ExecutiveMemoJobListData> {
    const qs = buildQuery({
      relay_status: joinQueryValues(params?.relayStatus),
      limit: params?.limit,
      cursor: params?.cursor,
    });
    const path = buildPath(`/agents/executive-memos/jobs${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const rawItems =
        getByPath(data, "items") ??
        getByPath(data, "jobs") ??
        getByPath(data, "lists") ??
        [];
      return {
        items: Array.isArray(rawItems) ? rawItems.map(normalizeExecutiveMemoJobStatus) : [],
        nextCursor: pickStringByPaths(data, ["nextCursor", "next_cursor"]) ?? null,
      };
    });
  },

  async getExecutiveMemoJob(jobId: string): Promise<ExecutiveMemoJobStatusData> {
    const path = buildPath(`/agents/executive-memos/jobs/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeExecutiveMemoJobStatus(data);
    });
  },

  async listExecutiveMemos(params?: {
    q?: string;
    kind?: string[];
    theme?: string[];
    tag?: string[];
    priority?: ExecutiveMemoPriority[];
    status?: "active" | "archived";
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
    sort?: "captured_at_desc" | "captured_at_asc" | "updated_at_desc";
  }): Promise<ExecutiveMemoListData> {
    const qs = buildQuery({
      q: params?.q,
      kind: joinQueryValues(params?.kind),
      theme: joinQueryValues(params?.theme),
      tag: joinQueryValues(params?.tag),
      priority: joinQueryValues(params?.priority),
      status: params?.status,
      from: params?.from,
      to: params?.to,
      cursor: params?.cursor,
      limit: params?.limit,
      sort: params?.sort,
    });
    const path = buildPath(`/executive-memos${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const rawItems =
        getByPath(data, "items") ??
        getByPath(data, "memos") ??
        getByPath(data, "lists") ??
        [];
      return {
        items: Array.isArray(rawItems) ? rawItems.map(normalizeExecutiveMemoListItem) : [],
        nextCursor: pickStringByPaths(data, ["nextCursor", "next_cursor"]) ?? null,
      };
    });
  },

  async getExecutiveMemo(memoId: string): Promise<ExecutiveMemoDetailData> {
    const path = buildPath(`/executive-memos/${encodeURIComponent(memoId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeExecutiveMemoDetail(data);
    });
  },

  async getExecutiveMemoSourceImage(memoId: string): Promise<ExecutiveMemoSourceImageData> {
    const path = buildPath(`/executive-memos/${encodeURIComponent(memoId)}/source-image`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const url = pickStringByPaths(data, ["url", "sourceUrl", "source_url", "downloadUrl", "download_url"]) ?? "";
      const expiresAt = pickStringByPaths(data, ["expiresAt", "expires_at"]) ?? "";

      if (!url) {
        throw new AppError("Invalid executive memo source image response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return { url, expiresAt };
    });
  },

  async updateExecutiveMemo(
    memoId: string,
    payload: ExecutiveMemoUpdatePayload
  ): Promise<ExecutiveMemoDetailData> {
    const path = buildPath(`/executive-memos/${encodeURIComponent(memoId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          version: payload.version,
          title: payload.title,
          body: payload.body,
          kind: payload.kind,
          themes: payload.themes,
          tags: payload.tags,
          priority: payload.priority,
          action_items: payload.actionItems?.map((item) => ({
            type: item.type,
            text: item.text,
          })),
          related_agents: payload.relatedAgents,
          status: payload.status,
        }),
      });
      return normalizeExecutiveMemoDetail(data);
    });
  },

  async deleteExecutiveMemo(memoId: string): Promise<void> {
    const path = buildPath(`/executive-memos/${encodeURIComponent(memoId)}`);

    await withAuthRetry(async (accessToken) => {
      await requestJson(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async createGroupTalkSession(params: CreateGroupTalkSessionParams): Promise<GroupTalkSessionResponse> {
    const path = buildPath("/group-talk/sessions");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkSessionResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template_id: params.templateId,
          title: params.title,
          description: params.description,
          members: params.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            role: member.role,
            ...(typeof member.stance === "string" || member.stance === null ? { stance: member.stance } : {}),
          })),
        }),
      });

      if (
        !data ||
        typeof data.session_id !== "string" ||
        !Array.isArray(data.members) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async recommendGroupTalk(consultationText: string): Promise<GroupTalkRecommendationResponse> {
    const path = buildPath("/group-talk/recommend");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkRecommendationResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          consultation_text: consultationText,
        }),
      });

      if (!data || typeof data !== "object") {
        throw new AppError("Invalid /group-talk/recommend response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async listGroupTalkSessions(): Promise<GroupTalkSessionHistoryListResponse> {
    const path = buildPath("/group-talk/sessions");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkSessionHistoryListResponse>(path, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!data || !Array.isArray(data.sessions)) {
        throw new AppError("Invalid /group-talk/sessions list response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async getGroupTalkSession(sessionId: string): Promise<GroupTalkSessionDetailResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkSessionDetailResponse>(path, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!data.session_id || !Array.isArray(data.members) || !Array.isArray(data.messages)) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId} response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async getGroupTalkSessionReport(sessionId: string): Promise<GroupTalkReportResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/report`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkReportResponse>(path, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!isGroupTalkReportResponse(data)) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/report response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "GET",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async generateGroupTalkSessionReport(sessionId: string): Promise<GroupTalkReportResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/report`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkReportResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!isGroupTalkReportResponse(data)) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/report response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async createGroupTalkTurn(sessionId: string, params: CreateGroupTalkTurnParams): Promise<GroupTalkTurnResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/turns`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkTurnResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_message: params.userMessage,
        }),
      });

      if (
        !data ||
        typeof data.session_id !== "string" ||
        typeof data.turn_id !== "string" ||
        !Array.isArray(data.messages) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/turns response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async createGroupTalkIntake(sessionId: string, params: CreateGroupTalkTurnParams): Promise<GroupTalkIntakeResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/intake`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<GroupTalkIntakeResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_message: params.userMessage,
        }),
      });

      if (
        !data ||
        typeof data.session_id !== "string" ||
        typeof data.turn_id !== "string" ||
        !Array.isArray(data.messages) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/intake response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  async createGroupTalkTurnComplete(
    sessionId: string,
    turnId: string,
    params: CompleteGroupTalkTurnParams
  ): Promise<GroupTalkTurnResponse> {
    const path = buildPath(`/group-talk/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/complete`);

    return await withAuthRetry(async (accessToken) => {
      const timeoutController = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timeoutId =
        timeoutController !== null
          ? setTimeout(() => timeoutController.abort(), GROUP_TALK_COMPLETE_CLIENT_TIMEOUT_MS)
          : null;

      let data: GroupTalkTurnResponse;
      try {
        data = await requestEnvelopeData<GroupTalkTurnResponse>(path, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          ...(timeoutController !== null ? { signal: timeoutController.signal } : {}),
          body: JSON.stringify({
            continue_token: params.continueToken,
          }),
        });
      } finally {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      }

      if (
        !data ||
        typeof data.session_id !== "string" ||
        typeof data.turn_id !== "string" ||
        !Array.isArray(data.messages) ||
        typeof data.created_at !== "string"
      ) {
        throw new AppError("Invalid /group-talk/sessions/{sessionId}/turns/{turnId}/complete response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }

      return data;
    });
  },

  /**
   * 商品一覧取得（コイン購入プラン）
   * @param storeType 1:App Store, 2:Google Play
   */
  async getProductList(storeType: 1 | 2): Promise<Product[]> {
    await this.ensureAuth();

    const qs = buildQuery({
      storeType: storeType.toString(),
    });

    const path = buildPath(`/product/list${qs}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<ProductListResponse>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const lists = (data.lists || []).map((item: any) => ({
        ...item,
        productId: item.productId ?? item.iapProductId ?? item.iap_product_id ?? item.id ?? "",
        price: Number(item.price ?? 0),
        coin: Number(item.coin ?? item.paidCoin ?? 0),
        free_coin: Number(item.freeCoin ?? item.free_coin ?? 0),
        isOneTime: Number(item.isOneTime ?? item.is_one_time ?? 0) as 0 | 1,
        isPurchased: Number(item.isPurchased ?? item.isPurchase ?? item.is_purchase ?? 0) as 0 | 1,
      }));

      return lists;
    });
  },

  /**
   * 商品購入（コイン購入）
   * @param params 購入パラメータ
   */
  async purchaseProduct(params: {
    productId: string;
    storeType: 1 | 2;
    transactionId?: string;
    purchaseToken?: string;
  }): Promise<void> {
    await this.ensureAuth();

    const body: ProductPurchaseRequest = {
      iapProductId: params.productId,
      storeType: params.storeType,
      transactionId: params.transactionId,
      purchaseToken: params.purchaseToken,
    };

    const path = buildPath("/product/purchase");

    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<ProductPurchaseResponse>(path, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    });
  },

  async createNewsJob(params: NewsJobRequest): Promise<NewsJobResponse> {
    const buildForm = (includeAdditionalFields: boolean) => {
      const form = new URLSearchParams();
      form.set("keyword", params.keyword);
      form.set("notificationType", params.notificationType);

      if (params.email) {
        form.set("email", params.email);
      }

      if (params.redirectUrl) {
        form.set("redirectUrl", params.redirectUrl);
      }

      if (includeAdditionalFields) {
        if (params.mode) {
          form.set("mode", params.mode);
        }

        if (params.readingProfile) {
          form.set("readingProfile", params.readingProfile);
        }

        if (params.deliveryOption) {
          form.set("deliveryFormat", params.deliveryOption);
        }

        if (params.userProfile) {
          form.set("userProfile[profile_text]", params.userProfile);
        }
      }

      return form;
    };

    const logNewsJobPayload = (
      form: URLSearchParams,
      attempt: 1 | 2,
      phase: "primary" | "fallback",
      when: "prepared" | "sending"
    ) => {
      if (process.env.NODE_ENV !== "development") return;
      const entries = Object.fromEntries(form.entries());
      const safe = { ...entries, email: entries.email ? "[REDACTED]" : undefined };
      console.log(`[api.createNewsJob] ${attempt}回目 ${phase} ${when} payload:`, safe);
    };

    const path = buildPath("/news/jobs");

    return await withAuthRetry(async (accessToken) => {
      const primaryForm = buildForm(true);
      const fallbackForm = buildForm(false);

      logNewsJobPayload(primaryForm, 1, "primary", "prepared");
      logNewsJobPayload(fallbackForm, 2, "fallback", "prepared");

      try {
        logNewsJobPayload(primaryForm, 1, "primary", "sending");

        const response = await requestEnvelopeData<NewsJobResponse>(path, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: primaryForm,
        });

        return response;
      } catch (error) {
        if (!(error instanceof AppError) || error.code !== "BAD_REQUEST") {
          throw error;
        }

        logNewsJobPayload(fallbackForm, 2, "fallback", "sending");

        return await requestEnvelopeData<NewsJobResponse>(path, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: fallbackForm,
        });
      }
    });
  },

  async getNewsJob(jobId: string): Promise<NewsJobStatusData> {
    const path = buildPath(`/news/jobs/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<NewsJobStatusData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async listNewsReportSubscriptions(): Promise<NewsReportSubscriptionList> {
    const path = buildPath("/news/subscriptions");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeNewsReportSubscriptionList(data);
    });
  },

  async createNewsReportSubscription(
    params: NewsReportSubscriptionCreateRequest
  ): Promise<NewsReportSubscription> {
    const path = buildPath("/news/subscriptions");

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });
      return normalizeNewsReportSubscription(data);
    });
  },

  async getNewsReportSubscription(subscriptionId: string): Promise<NewsReportSubscription> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeNewsReportSubscription(data);
    });
  },

  async patchNewsReportSubscription(
    subscriptionId: string,
    params: NewsReportSubscriptionUpdateRequest
  ): Promise<NewsReportSubscription> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });
      return normalizeNewsReportSubscription(data);
    });
  },

  async pauseNewsReportSubscription(subscriptionId: string): Promise<NewsReportSubscription> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}/pause`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeNewsReportSubscription(data);
    });
  },

  async resumeNewsReportSubscription(subscriptionId: string): Promise<NewsReportSubscription> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}/resume`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeNewsReportSubscription(data);
    });
  },

  async runNowNewsReportSubscription(subscriptionId: string): Promise<NewsReportSubscriptionRunNowResponse> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}/run-now`);

    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<unknown>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return normalizeNewsReportSubscriptionRunNowResponse(data);
    });
  },

  async deleteNewsReportSubscription(subscriptionId: string): Promise<void> {
    const path = buildPath(`/news/subscriptions/${encodeURIComponent(subscriptionId)}`);

    await withAuthRetry(async (accessToken) => {
      await requestJson<ApiEnvelope<unknown> | null>(path, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async searchCompanyResearchCandidates(
    params: CompanyResearchCandidateSearchInput
  ): Promise<CompanyResearchCandidateSearchResponse> {
    const path = buildPath("/agents/company-research/candidates");

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchCandidateSearchResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: params.company_name,
        }),
      });
    });
  },

  async createCompanyResearchQuickSummary(
    params: CompanyResearchQuickSummaryInput
  ): Promise<CompanyResearchQuickSummaryResponse> {
    const path = buildPath("/agents/company-research/quick-summary");

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchQuickSummaryResponse>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          search_id: params.search_id ?? undefined,
          candidate_id: params.candidate_id ?? undefined,
          company_name: params.company_name,
          company_url: params.company_url || null,
          source_url: params.source_url || null,
          location: params.location ?? "",
          representative_name: params.representative_name ?? "",
          business_summary: params.business_summary ?? "",
          industry: params.industry ?? "",
        }),
      });
    });
  },

  async createCompanyResearchJob(params: CompanyResearchCreateJobInput): Promise<CompanyResearchJobAccepted> {
    const path = buildPath("/agents/company-research/jobs");

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchJobAccepted>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: params.company_name || null,
          company_url: params.company_url || null,
          quick_summary_id: params.quick_summary_id ?? null,
          quick_summary: params.quick_summary ?? null,
          intent_type: params.intent_type,
          self_profile_id: params.self_profile_id ?? null,
          self_company_url: params.self_company_url || null,
          email_delivery: params.email_delivery ?? params.send_email ?? false,
          email_to: params.email_to ?? params.email ?? "",
          requested_by: params.requested_by ?? "",
          client_request_id: params.client_request_id ?? undefined,
        }),
      });
    });
  },

  async getCompanyResearchJob(jobId: string): Promise<CompanyResearchJobDetail> {
    const path = buildPath(`/agents/company-research/jobs/${encodeURIComponent(jobId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchJobDetail>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async listCompanyResearchJobs(params?: CompanyResearchJobListInput): Promise<CompanyResearchJobList> {
    const qs = buildQuery({
      status: params?.status,
      limit: params?.limit,
      cursor: params?.cursor,
    });
    const path = buildPath(`/agents/company-research/jobs${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchJobList>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async listCompanyResearchRecords(params?: CompanyResearchRecordListInput): Promise<CompanyResearchRecordList> {
    const qs = buildQuery({
      cursor: params?.cursor,
    });
    const path = buildPath(`/agents/company-research/records${qs}`);

    return await withAuthRetry(async (accessToken) => {
      try {
        return await requestJson<CompanyResearchRecordList>(path, {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch (error) {
        if (isEndpointMissing(error)) {
          return { records: [], next_cursor: null };
        }
        throw error;
      }
    });
  },

  async getCompanyResearchRecord(quickSummaryId: string): Promise<CompanyResearchRecordDetail> {
    const path = buildPath(`/agents/company-research/records/${encodeURIComponent(quickSummaryId)}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestJson<CompanyResearchRecordDetail>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getAgentJobs(params?: { resource?: AgentJobResource; status?: string; limit?: number; cursor?: string }): Promise<AgentJobsData> {
    const qs = buildQuery({
      resource: params?.resource,
      status: params?.status,
      limit: params?.limit,
      cursor: params?.cursor,
    });
    const path = buildPath(`/agent/jobs${qs}`);

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<AgentJobsData>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async getAgentJob(resource: AgentJobResource, jobId: string): Promise<AgentJobItem> {
    const path = buildPath(
      `/agent/jobs/${encodeURIComponent(resource)}/${encodeURIComponent(jobId)}`
    );

    return await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<AgentJobItem>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  },

  async googleSsoLogin(params: { idToken: string; invite_code?: string }): Promise<{
    email: string;
    userId?: string;
    secretKey?: string;
    accessToken?: string;
    refreshToken?: string;
    isNewUser?: boolean;
  }> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/google-sso");

    const body: Record<string, string> = {
      // backend validation requires this exact key name
      id_token: params.idToken,
      terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
      terminal_identification_number: deviceParams.terminalIdentificationNumber,
      deviceType: String(deviceParams.deviceType),
      device_type: String(deviceParams.deviceType),
    };
    if (params.invite_code) {
      body.invite_code = params.invite_code;
    }

    let data: GoogleSsoData;
    try {
      data = await requestEnvelopeData<GoogleSsoData>(path, {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (e) {
      if (e instanceof AppError && e.code === "BAD_REQUEST") {
        try {
          const parsed = JSON.parse(e.meta.responseText ?? "");
          if (parsed?.message === INVITE_CODE_REQUIRED_MESSAGE) {
            throw new AppError(e.message, "BAD_REQUEST", { ...e.meta, inviteCodeRequired: true });
          }
        } catch (inner) {
          if (inner instanceof AppError) throw inner;
        }
      }
      throw e;
    }

    if (!data.email) {
      throw new AppError("Invalid /auth/google-sso response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return {
      email: data.email,
      userId: data.userId,
      secretKey: data.secretKey,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      isNewUser: data.isNewUser,
    };
  },

  async startLineRegister(params: {
    redirectUri: string;
    inviteCode?: string;
    botPrompt?: "normal";
    disableAutoLogin?: boolean;
  }): Promise<LineRegisterStartData> {
    const path = buildPath("/line/register/start");

    const body: Record<string, string | boolean> = { redirect_uri: params.redirectUri };
    if (params.inviteCode) body.invite_code = params.inviteCode;
    if (params.botPrompt) body.bot_prompt = params.botPrompt;
    if (params.disableAutoLogin) body.disable_auto_login = true;

    const data = await requestLineRegisterEnvelope<any>(path, {
      method: "POST",
      body: JSON.stringify(body),
    });

    const authorizeUrl =
      pickStringByPaths(data, ["authorize_url", "authorizeUrl", "authorization_url", "authorizationUrl"]) ?? "";
    const expiresAt = pickStringByPaths(data, ["expires_at", "expiresAt"]) ?? null;

    if (!authorizeUrl) {
      throw new AppError("Invalid /line/register/start response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return { authorizeUrl, expiresAt };
  },

  async callbackLineRegister(params: {
    code: string;
    state: string;
    redirectUri: string;
  }): Promise<LineRegisterCallbackData> {
    const path = buildPath("/line/register/callback");

    const data = await requestLineRegisterEnvelope<any>(path, {
      method: "POST",
      body: JSON.stringify({
        code: params.code,
        state: params.state,
        redirect_uri: params.redirectUri,
      }),
    });

    const registrationToken = pickStringByPaths(data, ["registration_token", "registrationToken"]) ?? "";
    const expiresAt = pickStringByPaths(data, ["expires_at", "expiresAt"]) ?? null;

    if (!registrationToken) {
      throw new AppError("Invalid /line/register/callback response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return { registrationToken, expiresAt };
  },

  async completeLineRegister(params: {
    registrationToken: string;
  }): Promise<LineRegisterCompleteData> {
    const path = buildPath("/line/register/complete");

    const data = await requestLineRegisterEnvelope<any>(path, {
      method: "POST",
      body: JSON.stringify({
        registration_token: params.registrationToken,
      }),
    });

    const userId = pickStringByPaths(data, ["userId", "user_id"]) ?? "";
    const accessToken = pickStringByPaths(data, ["accessToken", "access_token"]) ?? "";
    const refreshToken = pickStringByPaths(data, ["refreshToken", "refresh_token"]) ?? undefined;
    const tokenType = pickStringByPaths(data, ["tokenType", "token_type"]) ?? "Bearer";
    const isNewUser = pickBooleanByPaths(data, ["isNewUser", "is_new_user"]) ?? true;

    if (!userId || !accessToken) {
      throw new AppError("Invalid /line/register/complete response.", "INVALID_RESPONSE", {
        endpoint: buildEndpoint(path),
        method: "POST",
        responseText: safeText(JSON.stringify(data)),
      });
    }

    return { userId, accessToken, refreshToken, tokenType, isNewUser };
  },

  async registerUserName(params: { name: string }): Promise<{ name: string }> {
    const path = buildPath("/user/name");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<{ name?: string }>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: params.name }),
      });
      if (!data.name) {
        throw new AppError("Invalid /user/name response.", "INVALID_RESPONSE", {
          endpoint: buildEndpoint(path),
          method: "POST",
          responseText: safeText(JSON.stringify(data)),
        });
      }
      return { name: data.name };
    });
  },

  async createBillingCheckoutSession(planCode?: string): Promise<BillingCheckoutSessionData> {
    const path = buildPath("/billing/checkout-sessions");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<BillingCheckoutSessionData>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(planCode ? { planCode } : {}),
      });
      return {
        provider: "stripe",
        sessionId: String(data?.sessionId ?? ""),
        url: String(data?.url ?? ""),
      };
    });
  },

  async createBillingPortalSession(): Promise<BillingPortalSessionData> {
    const path = buildPath("/billing/portal-sessions");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<BillingPortalSessionData>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return {
        provider: "stripe",
        url: String(data?.url ?? ""),
      };
    });
  },

  async getBillingSubscription(): Promise<BillingSubscriptionData> {
    const path = buildPath("/billing/subscription");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<any>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return {
        provider: "stripe",
        status: typeof data?.status === "string" ? data.status : null,
        plan: normalizePlanType(data?.plan),
        planCode: typeof data?.planCode === "string" ? data.planCode : null,
        currentPeriodEnd: typeof data?.currentPeriodEnd === "string" ? data.currentPeriodEnd : null,
        paidThroughAt: typeof data?.paidThroughAt === "string" ? data.paidThroughAt : null,
        cancelAtPeriodEnd: Boolean(data?.cancelAtPeriodEnd),
        managementAvailable: Boolean(data?.managementAvailable),
        accountState: normalizeAccountState(data?.accountState),
      };
    });
  },

  // ---- アカウント連携 (LINE SSO ユーザー向け) ----

  async getAccountLinkStatus(): Promise<{
    hasEmail: boolean;
    hasGoogle: boolean;
    hasLine: boolean;
    maskedMail: string | null;
  }> {
    const path = buildPath("/account/link/status");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<{
        hasEmail?: boolean;
        hasGoogle?: boolean;
        hasLine?: boolean;
        maskedMail?: string | null;
      }>(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return {
        hasEmail: data.hasEmail === true,
        hasGoogle: data.hasGoogle === true,
        hasLine: data.hasLine === true,
        maskedMail: typeof data.maskedMail === "string" ? data.maskedMail : null,
      };
    });
  },

  async requestAccountLinkEmailOtp(email: string): Promise<void> {
    const path = buildPath("/account/link/email/otp/request");
    await withAuthRetry(async (accessToken) => {
      await requestEnvelopeData<unknown>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
    });
  },

  async verifyAccountLinkEmailOtp(
    email: string,
    code: string
  ): Promise<{ errorCode?: string }> {
    const path = buildPath("/account/link/email/otp/verify");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<{ errorCode?: string }>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
      });
      return { errorCode: typeof data?.errorCode === "string" ? data.errorCode : undefined };
    });
  },

  async linkAccountGoogle(idToken: string): Promise<{ errorCode?: string }> {
    const path = buildPath("/account/link/google");
    return await withAuthRetry(async (accessToken) => {
      const data = await requestEnvelopeData<{ errorCode?: string }>(path, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id_token: idToken }),
      });
      return { errorCode: typeof data?.errorCode === "string" ? data.errorCode : undefined };
    });
  },

  async invalidateGoogleSso(params?: {
    scope?: "current_session";
    reason?: "user_initialize";
  }): Promise<GoogleSsoInvalidateResult> {
    const deviceParams = getDeviceParams();
    const path = buildPath("/auth/google-sso/invalidate");

    const data = await withAuthRetry(async (accessToken) => {
      return await requestEnvelopeData<any>(path, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          scope: params?.scope ?? "current_session",
          terminalIdentificationNumber: deviceParams.terminalIdentificationNumber,
          reason: params?.reason ?? "user_initialize",
        }),
      });
    });

    return {
      invalidated: Boolean(data?.invalidated ?? data?.alreadyInvalidated ?? true),
      alreadyInvalidated: data?.alreadyInvalidated === true ? true : undefined,
      effectiveFrom: typeof data?.effectiveFrom === "string" ? data.effectiveFrom : null,
    };
  },
};
